import { Dialog } from "@mui/material";
import { format, isSaturday, isSunday, startOfDay } from "date-fns";
import { Form, Formik } from "formik";
import _ from "lodash";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQueries } from "react-query";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import { getPlanDetails, manualForfeiture } from "../../api/Esop";
import AlertDialog from "../../components/shared/AlertDialog";
import { CollapsibleComponent } from "../../components/shared/Collapsible";
import Tooltip from "../../components/shared/Tooltip";
import {
  CheckBoxWithLabel,
  Input,
  Label,
} from "../../components/shared/InputField";
import LazyPdfDocument from "../../components/shared/LazyPdfDocument";
import { Select } from "../../components/shared/Select";
import { AccelerateVesting } from "../esopOverview/AccelerateVesting";
import SeparationReasons from "../../constants/SeparationReasons";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  ButtonSecondary,
  HStack,
  Notes,
  VStack,
} from "../../components/utils";
import ExtendExerciseDialog from "../esopOverview/ExtendExerciseDialog";
import {
  useAdminSignFafLetter,
  useCompleteResignation,
  useDiscardResignation,
  useEmployeePageDetails,
  useGetResignationDetails,
  useResignationProcess,
  useSendFafLetterToEmployee,
} from "../../queries/employees";
import { useGrants, useVestingTables } from "../../queries";
import {
  useGetEmailSettings,
  useGetGrantLetterSettings,
} from "../../queries/siteSettings";
import { useError } from "../../store/errorStore";
import { useAccelerateStore } from "../../store/useAccelerateStore";
import { useSignaturePadDialog } from "../../store/useDialogStore";
import { SwitchButton } from "../../components/shared/SwitchButton";
import { EmployementStatus, RevertResignation } from "../../types/Employee";
import { ExitDateBasis, ForfeitureSettings } from "../../types/EsopPlan";
import {
  FafLetterStatus,
  GrantDetailsForExercise,
  GrantDetailsForExtendExercise,
  GrantStatusForResignation,
  ResignationDto,
} from "../../types/EmployeeResignation";
import pdfLogo from "../../utils/assets/pdf.svg";
import convertToBase64 from "../../utils/convertToBase64";
import {
  getCurrencySymbol,
  getCurrencyType,
} from "../../utils/currencyFormatter";
import {
  formatDate,
  formatDisplayDate,
  parseDateString,
} from "../../utils/date";
import { downloadS3File } from "../../utils/DownloadFile";
import {
  computeLastDateToExercise,
  getExitRule,
  mapReasonToExitType,
} from "../../utils/forfeitureUtils";
import RevertResignationDialog from "../../components/shared/RevertResignationDialog";
import { InitiateResignation } from "../../modals/InitiateResignation";
import { UpdateLastWorkingDateDialog } from "../../modals/UpdateLastWorkingDateDialog";
import UploadFafLetterDialog from "../../modals/UploadFafLetterDialog";
import { options } from "../allPlans/GrantPrice";
import { AccelerateVestingDto } from "../../types/Grant";

export const Resignation = ({ setActiveTab }: { setActiveTab: Function }) => {
  const { id } = useParams();
  const _id = id ?? "";

  const { data: employee } = useEmployeePageDetails(_id);
  const { data: grantSettings } = useGetGrantLetterSettings();
  const { data: emailSettings } = useGetEmailSettings();
  const dateForLapsing = grantSettings?.dateForLapsing || "lastWorkingDay";

  const employeeName = employee?.employeeName ?? "";
  const isResignationComplete = employee?.status === EmployementStatus.RESIGNED;
  const [openField, setOpenField] = useState("");
  const [enablePauseVesting, setEnablePauseVesting] = useState(false);
  const [enableLastWorkingDay, setEnableLastWorkingDay] = useState(false);
  const [enableAccelerateVesting, setEnableAccelerateVesting] = useState(false);
  const [showLapsedToggle, setshowLapsedToggle] = useState<boolean>(false);

  const [enableExercise, setEnableExercise] = useState(false);
  const [enableSettlementLetter, setEnableSettlementLetter] = useState(false);
  const [loadingButton, setLoadingButton] = useState<string | null>(null);
  const [extendExerciseDlgOpen, setExtendExerciseDlgOpen] = useState(false);
  const [forfeitConfirmOpen, setForfeitConfirmOpen] = useState(false);
  const [forfeitLoading, setForfeitLoading] = useState(false);
  const [fafSignaturePending, setFafSignaturePending] = useState(false);
  const [useEsignForFaf, setUseEsignForFaf] = useState(false);
  const [isFafSigned, setIsFafSigned] = useState(false);
  const [sendEmailAfterSign, setSendEmailAfterSign] = useState(false);
  const hasManualFafEmailOverride = useRef(false);
  const [fafSignedAt, setFafSignedAt] = useState<Date | null>(null);
  const [fafEmailWasSent, setFafEmailWasSent] = useState(false);
  const [fafEmailSentTo, setFafEmailSentTo] = useState("");

  const { data: _resignationDetails, refetch: getResignationDetails } =
    useGetResignationDetails(_id);
  const { data: vestingData } = useVestingTables(_id);
  const { data: grantsResponse } = useGrants({ employeeId: _id });
  const { mutate: resignationProcess, isLoading: resignationProcessLoading } =
    useResignationProcess();
  const { mutate: completeResignation, isLoading: completeResignationLoading } =
    useCompleteResignation();
  const { mutate: discardResignation, isLoading: discardResignationLoading } =
    useDiscardResignation();
  const { mutate: adminSignFafLetter, isLoading: fafSignLoading } =
    useAdminSignFafLetter();
  const { mutate: sendFafLetterToEmployee, isLoading: fafSendLoading } =
    useSendFafLetterToEmployee();
  const { state: showSignaturePad, setState: setShowSignaturePad } =
    useSignaturePadDialog();

  const currencySymbol = getCurrencySymbol();
  const currencyType = getCurrencyType();

  const withdrawnGrants =
    grantsResponse?.grants?.filter(
      (grant: any) => grant.optionHolderState === "WITHDRAW"
    ) || [];

  const {
    data,
    setAccelerateData,
    getAccelerateData,
    addOrUpdateEntry,
    clearAccelerateData,
    removeEntry,
  } = useAccelerateStore();

  const enableAccelerationReasons = new Set<string | undefined>([
    "DEATH",
    "PERMANENT DISABILITY",
  ]);

  const accelerateStore = useAccelerateStore((state) =>
    state.getAccelerateData(_id)
  );

  const uniquePlanIds = useMemo(() => {
    const details =
      _resignationDetails?.initiationDetails?.grantTableDetails ?? [];
    const seen = new Set<string>();
    const result: string[] = [];
    for (const g of details) {
      if (g.esopPlanId && !seen.has(g.esopPlanId)) {
        seen.add(g.esopPlanId);
        result.push(g.esopPlanId);
      }
    }
    return result;
  }, [_resignationDetails?.initiationDetails?.grantTableDetails]);

  // Fetch full plan details for each unique plan (caches alongside the rest of
  // the app's plan queries via the same query key)
  const planDetailsQueries = useQueries(
    uniquePlanIds.map((planId) => ({
      queryKey: ["getPlanDetails", planId],
      queryFn: async () => (await getPlanDetails(planId)).data,
      enabled: !!planId,
    }))
  );

  const planDataKey = planDetailsQueries
    .map((q) => q.dataUpdatedAt ?? 0)
    .join(",");

  // Build planId -> ForfeitureSettings map from fetched results
  const planForfeitureMap = useMemo(() => {
    const map: Record<string, ForfeitureSettings> = {};
    planDetailsQueries.forEach((q, i) => {
      const settings = q.data?.forfeitureSettings;
      if (settings) map[uniquePlanIds[i]] = settings;
    });
    return map;
  }, [planDataKey, uniquePlanIds]);

  const resignationDetails = useMemo(() => {
    if (!_resignationDetails) return _resignationDetails;

    const resignationReason =
      _resignationDetails.initiationDetails.resignationReason;
    const resignationDate =
      _resignationDetails.initiationDetails.resignationDate;
    const lastWorkingDate =
      _resignationDetails.lastWorkingDayDetails?.lastWorkingDate;

    const updatedDetails = {
      ..._resignationDetails,
      accelerateVestingDetails: {
        ..._resignationDetails.accelerateVestingDetails,
        enableAccelerateVesting: accelerateStore.enabled,
      },
      initiationDetails: {
        ..._resignationDetails.initiationDetails,
        grantTableDetails:
          _resignationDetails.initiationDetails.grantTableDetails.map(
            (grant) => {
              const entry = accelerateStore.entries?.[grant.id];
              return {
                ...grant,
                noOfOptionsToAccelerate:
                  entry?.numberOfOptionsToBeAccelerated ||
                  grant.noOfOptionsToAccelerate,
                accelerateDate: entry?.accelerateDate || grant.accelerateDate,
                newExpiryDate: (() => {
                  // Compute the projected exercise expiry from plan forfeiture
                  // settings so the grant table reflects the correct next
                  // expiry date without waiting for a server round-trip.
                  const forfeitureSettings =
                    planForfeitureMap[grant.esopPlanId];
                  if (!forfeitureSettings) return grant.newExpiryDate;
                  const exitType = mapReasonToExitType(resignationReason);
                  if (!exitType) return grant.newExpiryDate;
                  const rule = getExitRule(forfeitureSettings, exitType);
                  if (!rule || rule.vestedOptions.exerciseWindow.neverExpires)
                    return grant.newExpiryDate;
                  // Choose basis date: last working date when the plan is
                  // configured for it, otherwise resignation date.
                  const useLWD =
                    forfeitureSettings.exitDateBasis ===
                      ExitDateBasis.LAST_WORKING_DATE && !!lastWorkingDate;
                  const basisStr = useLWD ? lastWorkingDate : resignationDate;
                  if (!basisStr) return grant.newExpiryDate;
                  const basisDate = parseDateString(basisStr);
                  if (Number.isNaN(basisDate.getTime()))
                    return grant.newExpiryDate;
                  const computed = computeLastDateToExercise(
                    basisDate,
                    forfeitureSettings,
                    resignationReason
                  );
                  return computed
                    ? format(computed, "yyyy-MM-dd")
                    : grant.newExpiryDate;
                })(),
              };
            }
          ),
      },
      pauseVestingDetails: {
        ..._resignationDetails.pauseVestingDetails,
        pauseVestingDate: _resignationDetails.pauseVestingDetails
          .pauseVestingDate
          ? _resignationDetails.pauseVestingDetails.pauseVestingDate
          : format(new Date(), "yyyy-MM-dd"),
      },
    };

    return updatedDetails;
  }, [_resignationDetails, planForfeitureMap]);

  // Compute whether we should show the "Forfeit" panel:
  // - hasExpiredNotForfeited: some grants have expiry date in the past and not forfeited
  const showForfeitPanel = useMemo(() => {
    const final = resignationDetails?.finalGrantsStatus ?? [];
    const hasExpiredNotForfeited = final.some((g) => {
      const expiryStr = g.newExpiryDate || g.currentExpiryDate;
      if (!expiryStr) return false;
      const expiryDate = new Date(expiryStr);
      // expired strictly before today
      const isExpired = startOfDay(expiryDate) <= startOfDay(new Date());
      const notForfeited = Number(g.forfeited || 0) === 0;
      return isExpired && notForfeited;
    });
    return hasExpiredNotForfeited;
  }, [resignationDetails]);

  const isPausedVestingDisabled =
    employee?.status === EmployementStatus.RESIGNED ||
    resignationDetails?.initiationDetails.grantTableDetails.length === 0;
  const [isFutureResignation, setIsFutureResignation] = useState(false);

  useEffect(() => {
    const selectedDate = new Date(
      resignationDetails?.lastWorkingDayDetails?.lastWorkingDate!
    ).setHours(0, 0, 0, 0);

    const today = new Date().setHours(0, 0, 0, 0);

    if (selectedDate === today) setshowLapsedToggle(true);
    else setshowLapsedToggle(false);
  }, []);

  useEffect(() => {
    setEnablePauseVesting(
      resignationDetails?.pauseVestingDetails?.enablePauseVesting || false
    );

    setEnableLastWorkingDay(
      resignationDetails?.lastWorkingDayDetails?.setLastWorkingDay || false
    );

    setEnableAccelerateVesting(
      resignationDetails?.accelerateVestingDetails?.enableAccelerateVesting ||
        false
    );

    setEnableExercise(
      resignationDetails?.exerciseOptionsDetails?.allowExercise || false
    );
    setEnableSettlementLetter(
      resignationDetails?.fafLetterDetails?.generateLetter || false
    );
    const isLastWorkingDateFuture = !!(
      resignationDetails?.lastWorkingDayDetails?.lastWorkingDate &&
      resignationDetails.lastWorkingDayDetails.lastWorkingDate !== "" &&
      new Date(resignationDetails.lastWorkingDayDetails.lastWorkingDate) >
        new Date()
    );

    const isResignationDateFuture = !!(
      resignationDetails?.initiationDetails?.resignationDate &&
      resignationDetails.initiationDetails.resignationDate !== "" &&
      new Date(resignationDetails.initiationDetails.resignationDate) >
        new Date()
    );

    setIsFutureResignation(
      (isLastWorkingDateFuture &&
        resignationDetails?.lastWorkingDayDetails?.setLastWorkingDay) ||
        isResignationDateFuture
    );
    setIsHoliday(false);

    const fafMeta = resignationDetails?.fafLetterDetails?.fafMetaData;
    const alreadySigned = !!fafMeta?.isAdminSigned;
    if (alreadySigned) {
      setIsFafSigned(true);
      if (fafMeta?.adminSignedAt) {
        setFafSignedAt(new Date(fafMeta.adminSignedAt));
      }
    } else {
      setIsFafSigned(false);
      setFafSignedAt(null);
    }
    if (fafMeta?.isEmailSent) {
      setFafEmailWasSent(true);
      if (fafMeta?.sentTo) {
        setFafEmailSentTo(fafMeta.sentTo);
      }
    } else {
      setFafEmailWasSent(false);
      setFafEmailSentTo("");
    }
  }, [resignationDetails]);

  const errorMessage = useError();

  useEffect(() => {
    if (
      !hasManualFafEmailOverride.current &&
      emailSettings?.sendFullAndFinalLetterToEmployees !== undefined
    ) {
      setSendEmailAfterSign(emailSettings.sendFullAndFinalLetterToEmployees);
    }
  }, [emailSettings?.sendFullAndFinalLetterToEmployees]);

  useEffect(() => {
    hasManualFafEmailOverride.current = false;
  }, [_id]);

  // React to signature pad result when in FAF letter signing mode
  useEffect(() => {
    if (!fafSignaturePending) return;
    if (
      showSignaturePad.signatureType === "regular" &&
      showSignaturePad.signature
    ) {
      let signature = showSignaturePad.signature;
      if (signature.includes("data:image/png;base64,")) {
        signature = signature.split(",")[1];
      }
      setFafSignaturePending(false);
      setShowSignaturePad({ open: false });
      adminSignFafLetter(
        {
          employeeId: _id,
          signatureBase64: signature,
          sendEmail: sendEmailAfterSign,
        },
        {
          onSuccess: () => {
            const signedAt = new Date();
            setIsFafSigned(true);
            setFafSignedAt(signedAt);
            if (sendEmailAfterSign) {
              setFafEmailWasSent(true);
            }
            toast(
              sendEmailAfterSign
                ? "Settlement Letter signed and sent to employee"
                : "Settlement Letter signed successfully",
              { type: "success", autoClose: 2000 }
            );
            getResignationDetails();
          },
          onError: (err: any) => {
            const errMsg =
              err?.response?.data?.reason ||
              err?.response?.data?.errorsValidation?.errorRaw ||
              "Failed to sign the settlement letter";
            toast(errMsg, { type: "error", autoClose: 2000 });
          },
        }
      );
    } else if (showSignaturePad.signatureType === "esign") {
      setFafSignaturePending(false);
      setShowSignaturePad({ open: false });
    }
  }, [showSignaturePad]);

  const handleFafAdminSign = () => {
    setFafEmailWasSent(false);
    setFafEmailSentTo("");
    setFafSignaturePending(true);
    setShowSignaturePad({
      open: true,
      entityType: "fafLetter",
      userName: employeeName,
    });
  };

  const handleSendFafLetter = () => {
    sendFafLetterToEmployee(
      { employeeId: _id, useEsign: useEsignForFaf },
      {
        onSuccess: () => {
          toast(
            useEsignForFaf
              ? "Settlement Letter sent for digital signing"
              : "Settlement Letter sent to employee",
            { type: "success", autoClose: 2000 }
          );
        },
        onError: (err: any) => {
          const errMsg =
            err?.response?.data?.reason ||
            err?.response?.data?.errorsValidation?.errorRaw ||
            "Failed to send the settlement letter";
          toast(errMsg, { type: "error", autoClose: 2000 });
        },
      }
    );
  };

  const [dialog, setDialog] = useState<{
    open: boolean;
    index?: number;
    data?: string | RevertResignation;
    mode?:
      | "View Settlement Letter"
      | "Confirm Resignation"
      | "Confirm LWD"
      | "Confirm Discard"
      | "Confirm LWD after Today"
      | "Confirm LWD before Today"
      | "Accelerate Vesting";
    grant?: any;
  }>({
    open: false,
  });

  const [localAccelerateDialog, setLocalAccelerateDialog] = useState<{
    open: boolean;
    grant?: any;
  }>({ open: false });

  const [updateLWDDialog, setUpdateLWDDialog] = useState<{
    open: boolean;
  }>({ open: false });

  const completeResignationProcess = () => {
    completeResignation(_id, {
      onSuccess: () => {
        setActiveTab("Grant Details");
        toast("Completed Resignation Successfully", {
          type: "success",
          autoClose: 2000,
        });
        setDialog({
          open: false,
        });
      },
      onError: (err: any) => {
        const errMsg = err.response?.data?.errorsValidation?.errorRaw;
        if (errMsg) {
          errorMessage.setMessage(errMsg);
          toast(errMsg, {
            type: "error",
            autoClose: 2000,
          });
        }
        setDialog({
          open: false,
        });
      },
    });
  };

  const handleLWD = (values: ResignationDto, lapsedCheckbox = false) => {
    const defaultResignationDetails = getDefaultResignationProcessObject(_id);
    defaultResignationDetails.lastWorkingDayDetails =
      values.lastWorkingDayDetails;
    defaultResignationDetails.lastWorkingDayDetails.setLastWorkingDay =
      enableLastWorkingDay;

    resignationProcess(defaultResignationDetails, {
      onSuccess: () => {
        if (!lapsedCheckbox) {
          toast("Updated Last Working Day", {
            type: "success",
            autoClose: 2000,
          });
        } else {
          const lapseMessage = defaultResignationDetails.lastWorkingDayDetails
            .lapsedImmediately
            ? "Lapse immediately is enabled"
            : "Lapse immediately is disabled";

          toast(lapseMessage, {
            type: "success",
            autoClose: 2000,
          });
        }
        setDialog({
          open: false,
        });
        // React Query will automatically refetch the resignation details
        // after the query invalidation in the mutation
      },
      onError: (err: any) => {
        const errMsg = err.response?.data?.errorsValidation?.errorRaw;
        if (errMsg) {
          errorMessage.setMessage(errMsg);
          toast(errMsg, {
            type: "error",
            autoClose: 2000,
          });
        }
        setDialog({
          open: false,
        });
      },
    });
  };

  const handleSubmit = () => {
    setDialog({
      open: true,
      mode: "Confirm Resignation",
    });
  };

  const handlePauseVesting = (values: ResignationDto) => {
    const defaultResignationDetails = getDefaultResignationProcessObject(_id);
    defaultResignationDetails.pauseVestingDetails = values.pauseVestingDetails;
    defaultResignationDetails.pauseVestingDetails.enablePauseVesting =
      enablePauseVesting;
    resignationProcess(defaultResignationDetails, {
      onSuccess: () => {
        toast("Updated Pause Vesting Date", {
          type: "success",
          autoClose: 2000,
        });
        // React Query will automatically refetch after invalidation
      },
      onError: (err: any) => {
        const errMsg = err.response?.data?.errorsValidation?.errorRaw;
        if (errMsg) {
          errorMessage.setMessage(errMsg);
          toast(errMsg, {
            type: "error",
            autoClose: 2000,
          });
        }
      },
    });
  };

  const handleResignationDate = (values: ResignationDto) => {
    const defaultResignationDetails = getDefaultResignationProcessObject(_id);
    defaultResignationDetails.initiationDetails.resignationDate =
      values.initiationDetails.resignationDate;
    defaultResignationDetails.initiationDetails.saveDetails = true;
    resignationProcess(defaultResignationDetails, {
      onSuccess: () => {
        toast("Updated Resignation Date", {
          type: "success",
          autoClose: 2000,
        });
        // React Query will automatically refetch after invalidation
      },
      onError: (err: any) => {
        const errMsg = err.response?.data?.errorsValidation?.errorRaw;
        if (errMsg) {
          errorMessage.setMessage(errMsg);
          toast(errMsg, {
            type: "error",
            autoClose: 2000,
          });
        }
      },
    });
  };

  const handleExercise = (values: ResignationDto) => {
    const defaultResignationDetails = getDefaultResignationProcessObject(_id);
    defaultResignationDetails.exerciseOptionsDetails =
      values.exerciseOptionsDetails;
    defaultResignationDetails.exerciseOptionsDetails.allowExercise = true;
    defaultResignationDetails.exerciseOptionsDetails.getPreview = false;
    defaultResignationDetails.exerciseOptionsDetails.exerciseOptions = true;
    resignationProcess(defaultResignationDetails, {
      onSuccess: (data) => {
        toast("Exercise Request Sent Successfully", {
          type: "success",
          autoClose: 2000,
        });
        getResignationDetails();
      },
      onError: (err: any) => {
        const errMsg =
          err.response?.data?.errorsValidation?.errorRaw ||
          err.response?.data?.errorRaw;
        if (errMsg) {
          errorMessage.setMessage(errMsg);
          toast(errMsg, {
            type: "error",
            autoClose: 2000,
          });
        }
      },
    });
  };

  const handleGenerateSettlementLetter = (values: ResignationDto) => {
    const defaultResignationDetails = getDefaultResignationProcessObject(_id);
    defaultResignationDetails.fafLetterDetails = values.fafLetterDetails;
    defaultResignationDetails.fafLetterDetails.saveDetails = true;
    defaultResignationDetails.fafLetterDetails.generateLetter = true;
    defaultResignationDetails.fafLetterDetails.uploadFAFLetter = false;
    resignationProcess(defaultResignationDetails, {
      onSuccess: (data) => {
        toast("F&F Letter Generated Successfully", {
          type: "success",
          autoClose: 2000,
        });
        getResignationDetails();
        setLoadingButton(null);
      },
      onError: (err: any) => {
        const errMsg = err.response?.data?.errorsValidation?.errorRaw;
        if (errMsg) {
          errorMessage.setMessage(errMsg);
          toast(errMsg, {
            type: "error",
            autoClose: 2000,
          });
        }
      },
    });
  };

  const totalGranted = _.sumBy(
    resignationDetails?.initiationDetails.grantTableDetails,
    (g) => g.optionsGranted
  );

  const totalVested = _.sumBy(
    resignationDetails?.initiationDetails.grantTableDetails,
    (g) => g.optionsVested
  );
  const totalExercised = _.sumBy(
    resignationDetails?.initiationDetails.grantTableDetails,
    (g) => g.cashedOut + g.stockExercised
  );

  const isAcceleratedEnabled = useMemo(() => {
    const reason = resignationDetails?.initiationDetails?.resignationReason;
    if (reason && enableAccelerationReasons.has(reason)) return true;
    if (totalGranted - totalVested <= 0) return true;
    return false;
  }, [
    resignationDetails?.initiationDetails.resignationReason,
    totalGranted,
    totalVested,
  ]);

  const handleAccelerateAll = async () => {
    const allEntries = accelerateStore.entries;
    const pendingEntries = Object.values(allEntries || {}).filter(
      (e) => e.numberOfOptionsToBeAccelerated && e.accelerateDate
    );
    if (pendingEntries.length === 0) {
      toast("No saved entries to accelerate", { type: "info" });
      return;
    }
    const accelPayload = pendingEntries.map((entry) => {
      const grant =
        resignationDetails?.initiationDetails.grantTableDetails.find(
          (g) => g.id === entry.grantId
        );
      return {
        grantId: entry.grantId,
        numberOfOptionsToBeAccelerated:
          entry.numberOfOptionsToBeAccelerated || 0,
        accelerateDate: entry.accelerateDate || "",
        currentVested: grant?.optionsVested || 0,
        availableToAccelerate: Math.max(
          0,
          (grant?.optionsGranted ?? 0) - (grant?.optionsVested ?? 0)
        ),
        duringResignation: true,
      };
    });
    try {
      const defaultResignationDetails = getDefaultResignationProcessObject(_id);
      defaultResignationDetails.accelerateVestingDetails = {
        enableAccelerateVesting: Boolean(accelerateStore.enabled),
        accelerateVestingForGrants: accelPayload,
      };
      resignationProcess(defaultResignationDetails, {
        onSuccess: () => {
          toast("Accelerated all saved grants", { type: "success" });
          clearAccelerateData(_id);
          getResignationDetails();
        },
        onError: (err: any) => {
          const errMsg =
            err?.response?.data?.errorsValidation?.errorRaw || err?.message;
          if (errMsg) toast(errMsg, { type: "error" });
          else
            toast("One or more accelerations failed", {
              type: "error",
            });
        },
      });
    } catch (err: any) {
      const errMsg = err?.message || "One or more accelerations failed";
      toast(errMsg, { type: "error" });
    }
  };

  const handleSaveAccelerateEntry = (values: AccelerateVestingDto) => {
    const previousStore = { ...accelerateStore };
    try {
      const entry: AccelerateVestingDto = {
        currentVested: values.currentVested,
        availableToAccelerate: values.availableToAccelerate,
        grantId: values.grantId,
        numberOfOptionsToBeAccelerated:
          values.numberOfOptionsToBeAccelerated || 0,
        accelerateDate: values.accelerateDate,
      };
      addOrUpdateEntry(_id, entry);
      resignationDetails?.initiationDetails.grantTableDetails.forEach(
        (grant) => {
          if (grant.id === values.grantId) {
            grant.noOfOptionsToAccelerate =
              values.numberOfOptionsToBeAccelerated || 0;
            grant.accelerateDate = values.accelerateDate || "";
          }
        }
      );
    } catch (error: any) {
      setAccelerateData(_id, previousStore);
      toast(error?.message || "Failed to save accelerate entry", {
        type: "error",
      });
    }
    setLocalAccelerateDialog({ open: false });
  };

  async function handleUpload(file: File) {
    const base64File = (await convertToBase64(file)) as string;
    if (resignationDetails) {
      const defaultResignationDetails = getDefaultResignationProcessObject(_id);
      defaultResignationDetails.fafLetterDetails =
        resignationDetails.fafLetterDetails;
      defaultResignationDetails.fafLetterDetails.base64File = base64File;
      defaultResignationDetails.fafLetterDetails.fileName = file.name;
      defaultResignationDetails.fafLetterDetails.fileContentType = file.type;
      defaultResignationDetails.fafLetterDetails.saveDetails = true;
      defaultResignationDetails.fafLetterDetails.generateLetter = false;
      defaultResignationDetails.fafLetterDetails.uploadFAFLetter = true;
      resignationProcess(defaultResignationDetails, {
        onSuccess: () => {
          toast("F&F Letter Uploaded Successfully", {
            type: "success",
            autoClose: 2000,
          });
          setFafUploadDialogOpen(false);
          getResignationDetails();
        },
        onError: (err: any) => {
          const errMsg = err.response?.data?.errorsValidation?.errorRaw;
          if (errMsg) {
            errorMessage.setMessage(errMsg);
            toast(errMsg, {
              type: "error",
              autoClose: 2000,
            });
          }
        },
      });
    }
  }

  const isDiscardDisabled =
    enablePauseVesting ||
    enableExercise ||
    enableLastWorkingDay ||
    enableSettlementLetter;
  const handleDiscardResignation = () => {
    setDialog({
      open: true,
      mode: "Confirm Discard",
      data: {
        employeeIds: [_id],
        note: "Discard Resignation",
        revertAccessBackToPrimaryEmailId: false,
        sendEmailForVestings: false,
        vestAsPerToday: true,
        resumeFutureVestings: false,
      } as RevertResignation,
    });
  };

  const [fafUploadDialogOpen, setFafUploadDialogOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState({ open: false });
  const [isHoliday, setIsHoliday] = useState(false);
  const [displayMessage, setDisplaymessage] = useState("");

  return (
    <VStack className="bg-white text-gray-dark">
      <UploadFafLetterDialog
        open={fafUploadDialogOpen}
        onClose={() => setFafUploadDialogOpen(false)}
        onUpload={handleUpload}
        uploading={resignationProcessLoading}
      />
      <Dialog open={dialogOpen.open} maxWidth="md">
        <InitiateResignation
          onClose={() => setDialogOpen({ open: !dialogOpen.open })}
          onPrimaryAction={() => {
            setActiveTab("Resignation");
            setDialogOpen({ open: !dialogOpen.open });
          }}
          dateOfJoining={new Date(employee?.dateOfJoin || new Date())}
        />
      </Dialog>
      {!isResignationComplete ? (
        <HStack className="justify-between p-4 border-b border-solid">
          <Box className="flex flex-col ">
            <p className="text-lg font-semibold text-gray-600">
              Resignation Activities
            </p>
          </Box>
          <ButtonSecondary
            onClick={handleDiscardResignation}
            loading={discardResignationLoading}
          >
            Discard Resignation
          </ButtonSecondary>
        </HStack>
      ) : (
        <HStack className="justify-start p-4 border-b border-solid">
          <Box className="flex flex-col ">
            <p className="text-lg font-semibold text-gray-600">
              Resignation Summary
            </p>
          </Box>
        </HStack>
      )}
      {!isResignationComplete && resignationDetails && (
        <Formik
          key="resignationDetails"
          enableReinitialize={true}
          initialValues={resignationDetails}
          onSubmit={(values) => {
            handleSubmit();
          }}
        >
          {(formik) => (
            <Form key="resignationDetails">
              <Dialog
                open={dialog.open}
                onClose={() => {
                  setDialog({
                    open: false,
                  });
                }}
                maxWidth="md"
              >
                {dialog.mode === "View Settlement Letter" && dialog.data ? (
                  <LazyPdfDocument url={dialog.data as string} />
                ) : dialog.mode === "Confirm Resignation" ? (
                  <AlertDialog
                    message={"Are you sure you want to Complete Resignation?"}
                    error={false}
                    status={false}
                    primaryActionText="Submit"
                    secondaryActionText="Cancel"
                    onPrimaryAction={() => {
                      completeResignationProcess();
                    }}
                    onSecondaryAction={() => {
                      setDialog({
                        open: false,
                      });
                    }}
                    open={true}
                    loading={completeResignationLoading}
                    notesElement={
                      (!showLapsedToggle ||
                        formik.values.lastWorkingDayDetails
                          .lapsedImmediately) &&
                      (!formik.values.lastWorkingDayDetails.lastWorkingDate ||
                        startOfDay(
                          new Date(
                            formik.values.lastWorkingDayDetails.lastWorkingDate
                          )
                        ) <= startOfDay(new Date())) ? (
                        <div className="italic text-gray-500 mt-2 mb-1">
                          * Note: All unvested options will be lapsed
                          immediately
                        </div>
                      ) : null
                    }
                  />
                ) : dialog.mode === "Confirm LWD" ? (
                  <AlertDialog
                    message={
                      "Are you sure you would like to save Last Working Day without Pause Vesting?"
                    }
                    error={false}
                    status={false}
                    primaryActionText="Save"
                    secondaryActionText="Cancel"
                    onPrimaryAction={() => {
                      handleLWD(formik.values);
                    }}
                    onSecondaryAction={() => {
                      setDialog({
                        open: false,
                      });
                    }}
                    open={true}
                    loading={resignationProcessLoading}
                  />
                ) : dialog.mode === "Confirm LWD after Today" ? (
                  <AlertDialog
                    message={`Are you sure you want ${format(
                      new Date(
                        formik.values.lastWorkingDayDetails.lastWorkingDate
                      ),
                      "dd-MM-yyyy"
                    )} to be the Last Working Date?`}
                    error={false}
                    status={false}
                    notesElement={
                      <div className="mt-4 mb-3 text-sm text-gray-500">
                        Note: Setting this as the Last Working Date will resume
                        vesting until today upon manually resuming the vesting.
                      </div>
                    }
                    primaryActionText="Save"
                    secondaryActionText="Cancel"
                    onPrimaryAction={() => {
                      handleLWD(formik.values);
                    }}
                    onSecondaryAction={() => {
                      setDialog({
                        open: false,
                      });
                    }}
                    open={true}
                    loading={resignationProcessLoading}
                  />
                ) : dialog.mode === "Confirm LWD before Today" ? (
                  <AlertDialog
                    message={`Are you sure you want set ${format(
                      new Date(
                        formik.values.lastWorkingDayDetails.lastWorkingDate
                      ),
                      "dd-MM-yyyy"
                    )} as Last Working Date?
                    `}
                    error={false}
                    status={false}
                    notesElement={
                      <div className="mt-4 mb-3 text-sm text-gray-500">
                        Note: Setting this as the Last Working Date will resume
                        vesting until that date, upon manually resuming the
                        vesting.
                      </div>
                    }
                    primaryActionText="Save"
                    secondaryActionText="Cancel"
                    onPrimaryAction={() => {
                      handleLWD(formik.values);
                    }}
                    onSecondaryAction={() => {
                      setDialog({
                        open: false,
                      });
                    }}
                    open={true}
                    loading={resignationProcessLoading}
                  />
                ) : dialog.mode === "Confirm Discard" && dialog.data ? (
                  <RevertResignationDialog
                    open={dialog.open}
                    onClose={() =>
                      setDialog({
                        open: false,
                        data: "",
                      })
                    }
                    revertResignation={dialog.data as RevertResignation}
                    onSecondaryAction={() => {
                      setDialog({
                        open: false,
                        data: "",
                      });
                      setActiveTab("Grant Details");
                      getResignationDetails();
                    }}
                    isRevertResignation={false}
                  />
                ) : (
                  <div></div>
                )}
              </Dialog>
              <VStack className="p-4">
                <VStack className="gap-4 mb-8">
                  <Box className="p-4 rounded-md shadow-lg relative">
                    <Box className="font-medium text-md text-gray-700 mb-2">
                      Resignation Details
                    </Box>
                    <div className="absolute right-5 top-5">
                      <ButtonPrimary1
                        className={"cursor-pointer"}
                        onClick={() => {
                          setDialogOpen({
                            open: true,
                          });
                        }}
                      >
                        Edit Resignation Details
                      </ButtonPrimary1>
                    </div>

                    {/* Indent content to match Pause Vesting */}
                    <div className="pl-6">
                      <HStack className="items-center pt-4">
                        <Label className="mr-20 text-sm font-normal whitespace-nowrap">
                          Resignation Date
                        </Label>
                        <Input
                          type="date"
                          className={`mr-3 w-fit`}
                          {...formik.getFieldProps(
                            "initiationDetails.resignationDate"
                          )}
                          disabled={true}
                        />
                      </HStack>
                      {formik.values.initiationDetails?.resignationDate &&
                        formik.values.initiationDetails?.resignationReason && (
                          <HStack className="items-start w-full pt-4">
                            <Label className="mr-11 text-sm font-normal whitespace-nowrap pt-2">
                              Reason for resignation
                            </Label>
                            <div className="flex-1">
                              <div className={"p-3 form-input w-56 text-sm"}>
                                {SeparationReasons.find(
                                  (s) =>
                                    s.value ===
                                    formik.values.initiationDetails
                                      .resignationReason
                                )?.label ||
                                  formik.values.initiationDetails
                                    .resignationReason}
                              </div>
                            </div>
                          </HStack>
                        )}
                      <div className="mb-4 mt-2.5">
                        {formik.values.initiationDetails.grantTableDetails
                          .length > 0 && (
                          <GrantStatusTable
                            grantStatus={
                              formik.values.initiationDetails.grantTableDetails
                            }
                            currencySymbol={currencySymbol}
                            currencyType={currencyType}
                          />
                        )}
                      </div>
                    </div>
                  </Box>
                  {totalGranted - totalVested > 0 && (
                    <CollapsibleComponent
                      label={
                        openField !== "Pause Vesting"
                          ? `Would you like to pause vesting for ${employeeName}?`
                          : "Pause Vesting"
                      }
                      labelDetail={``}
                      isOpen={openField !== "Pause Vesting"}
                      onClick={() =>
                        openField !== "Pause Vesting"
                          ? setOpenField("Pause Vesting")
                          : setOpenField("")
                      }
                    >
                      <VStack className="gap-4">
                        <VStack className="">
                          <p className="font-medium text-md">
                            Would you like to pause vesting for {employeeName} ?
                          </p>
                          <p className="">
                            Pausing vesting would mean that no further options
                            will be vested for the Employee.
                          </p>
                        </VStack>
                        <HStack className="gap-40">
                          <HStack className="items-center gap-2">
                            <input
                              type="radio"
                              className={`accent-orange-501 outline-hidden mr-1
                cursor-pointer
                `}
                              checked={enablePauseVesting}
                              onChange={(e) => {
                                setEnablePauseVesting(e.target.checked);
                              }}
                            />
                            <Label className="mt-2">Yes</Label>
                          </HStack>
                          <HStack className="items-center gap-2">
                            <input
                              type="radio"
                              className={`accent-orange-501 outline-hidden mr-1
                cursor-pointer`}
                              checked={!enablePauseVesting}
                              onChange={(e) => {
                                setEnablePauseVesting(!e.target.checked);
                              }}
                            />
                            <Label className="mt-2">No</Label>
                          </HStack>
                        </HStack>
                        {enablePauseVesting && (
                          <>
                            <HStack className="items-center">
                              <Label className="mr-10 text-sm font-normal whitespace-nowrap">
                                Pause Vesting Date
                              </Label>
                              <Input
                                type="date"
                                className={`mr-3 w-fit ${
                                  isPausedVestingDisabled &&
                                  "cursor-not-allowed"
                                }`}
                                disabled={isPausedVestingDisabled}
                                {...formik.getFieldProps(
                                  "pauseVestingDetails.pauseVestingDate"
                                )}
                              />
                              <ButtonPrimary1
                                disabled={isPausedVestingDisabled}
                                className={"cursor-pointer"}
                                onClick={() => {
                                  handlePauseVesting(formik.values);
                                }}
                              >
                                Save
                              </ButtonPrimary1>
                            </HStack>
                            {formik.values.initiationDetails.grantTableDetails
                              .length > 0 && (
                              <PauseVestingGrantTable
                                grantStatus={
                                  formik.values.initiationDetails
                                    .grantTableDetails
                                }
                                currencyType={currencyType}
                              />
                            )}
                          </>
                        )}
                      </VStack>
                    </CollapsibleComponent>
                  )}

                  <CollapsibleComponent
                    label={
                      openField !== "LWD"
                        ? `Would you like to set last working day for ${employeeName}?`
                        : "Last Working Day"
                    }
                    labelDetail={``}
                    isOpen={openField !== "LWD"}
                    onClick={() =>
                      openField !== "LWD"
                        ? setOpenField("LWD")
                        : setOpenField("")
                    }
                  >
                    <VStack className="gap-4">
                      <VStack className="">
                        <p className="font-medium text-md">
                          Would you like to set last working day for{" "}
                          {employeeName}?
                        </p>
                      </VStack>
                      <HStack className="gap-40">
                        <HStack className="items-center gap-2">
                          <input
                            type="radio"
                            className={`accent-orange-501 outline-hidden mr-1
                cursor-pointer`}
                            checked={enableLastWorkingDay}
                            onChange={(e) =>
                              setEnableLastWorkingDay(e.target.checked)
                            }
                          />
                          <Label className="mt-2">Yes</Label>
                        </HStack>
                        <HStack className="items-center gap-2">
                          <input
                            type="radio"
                            className={`accent-orange-501 outline-hidden mr-1
                cursor-pointer`}
                            checked={!enableLastWorkingDay}
                            onChange={(e) =>
                              setEnableLastWorkingDay(!e.target.checked)
                            }
                          />
                          <Label className="mt-2">No</Label>
                        </HStack>
                      </HStack>
                      {enableLastWorkingDay ? (
                        <>
                          <HStack className="items-center">
                            <Label className="mr-10 text-sm font-normal whitespace-nowrap">
                              Last Working Day
                            </Label>
                            <Input
                              type="date"
                              className={`w-fit mr-3`}
                              {...formik.getFieldProps(
                                "lastWorkingDayDetails.lastWorkingDate"
                              )}
                              disabled={true}
                            />
                            <ButtonPrimary1
                              className={"cursor-pointer"}
                              onClick={() => {
                                setUpdateLWDDialog({ open: true });
                              }}
                            >
                              Edit
                            </ButtonPrimary1>
                          </HStack>
                        </>
                      ) : resignationDetails.pauseAndRevertOnResignationDate ===
                        false ? (
                        <></>
                      ) : (
                        <Notes
                          text={`If LWD is not set then Resignation Date will be
                            considered as Last Working Day`}
                        />
                      )}
                    </VStack>
                    {showLapsedToggle ? (
                      <div className="py-2">
                        <CheckBoxWithLabel
                          className="w-4 h-4 mr-1 cursor-pointer accent-orange-501 outline-hidden"
                          label={
                            "Checking this will lapse all the unvested options immediately."
                          }
                          id="lapse-checkbox"
                          labelclassname="text-gray-800 ml-2"
                          checked={
                            formik.values.lastWorkingDayDetails
                              .lapsedImmediately
                          }
                          onChange={(e) => {
                            const lastWorkingDayDetails = {
                              ...formik.values.lastWorkingDayDetails,
                              lapsedImmediately: e.target.checked,
                            };
                            formik.setFieldValue(
                              "lastWorkingDayDetails",
                              lastWorkingDayDetails
                            );
                            setUpdateLWDDialog({ open: true });
                          }}
                        />
                      </div>
                    ) : (
                      employee?.isPausedVesting &&
                      enableLastWorkingDay &&
                      (!formik.values.lastWorkingDayDetails.lastWorkingDate ||
                        startOfDay(
                          new Date(
                            formik.values.lastWorkingDayDetails.lastWorkingDate
                          )
                        ) <= startOfDay(new Date())) &&
                      formik.values.lastWorkingDayDetails.lastWorkingDate && (
                        <>
                          <Notes text="All unvested options will be lapsed immediately." />
                          <Notes
                            text={`The vesting will only resume until the last
                            working date i.e,
                            ${format(
                              new Date(
                                formik.values.lastWorkingDayDetails.lastWorkingDate
                              ),
                              "dd-MM-yyyy"
                            )}
                            .`}
                          />
                        </>
                      )
                    )}
                    {employee?.isPausedVesting &&
                      formik.values.lastWorkingDayDetails.lastWorkingDate &&
                      startOfDay(
                        new Date(
                          formik.values.lastWorkingDayDetails.lastWorkingDate
                        )
                      ) > startOfDay(new Date()) && (
                        <Notes
                          text="Vesting will resume until today. Vesting
                            might get impacted if the last working day is moved
                            to a date before today"
                        />
                      )}
                  </CollapsibleComponent>
                  {isAcceleratedEnabled && (
                    <CollapsibleComponent
                      label={
                        openField !== "Accelerate Vesting"
                          ? `Would you like to accelerate vesting for ${employeeName}?`
                          : "Accelerate Vesting"
                      }
                      labelDetail={``}
                      isOpen={openField !== "Accelerate Vesting"}
                      onClick={() =>
                        openField !== "Accelerate Vesting"
                          ? setOpenField("Accelerate Vesting")
                          : setOpenField("")
                      }
                    >
                      <VStack className="gap-4">
                        <VStack className="">
                          <p className="font-medium text-md">
                            Would you like to accelerate vesting for{" "}
                            {employeeName}?
                          </p>
                          <p className="">
                            Accelerating vesting will move vesting events
                            earlier for selected grants.
                          </p>
                        </VStack>
                        <HStack className="gap-40">
                          <HStack className="items-center gap-2">
                            <input
                              type="radio"
                              className="accent-orange-501 outline-hidden mr-1 cursor-pointer"
                              checked={enableAccelerateVesting}
                              onChange={(e) => {
                                const newVal = true;
                                setEnableAccelerateVesting(newVal);
                                setAccelerateData(_id, {
                                  ...accelerateStore,
                                  enabled: newVal,
                                });
                              }}
                            />
                            <Label className="mt-2">Yes</Label>
                          </HStack>
                          <HStack className="items-center gap-2">
                            <input
                              type="radio"
                              className="accent-orange-501 outline-hidden mr-1 cursor-pointer"
                              checked={!enableAccelerateVesting}
                              onChange={(e) => {
                                const newVal = false;
                                setEnableAccelerateVesting(newVal);
                                setAccelerateData(_id, {
                                  ...accelerateStore,
                                  enabled: newVal,
                                });
                              }}
                            />
                            <Label className="mt-2">No</Label>
                          </HStack>
                        </HStack>
                        {enableAccelerateVesting && (
                          <>
                            <div className="flex justify-end mb-2">
                              <ButtonPrimary1
                                className="w-fit"
                                onClick={() => {
                                  handleAccelerateAll();
                                }}
                                disabled={false}
                              >
                                Accelerate All
                              </ButtonPrimary1>
                            </div>
                            <AccelerateVestingTable
                              rows={
                                formik.values.initiationDetails
                                  .grantTableDetails
                              }
                              onEdit={(index) => {
                                const grant =
                                  formik.values.initiationDetails
                                    .grantTableDetails[index];

                                const existingEntry =
                                  accelerateStore.entries?.[grant.id];

                                const grantWithStoreData = {
                                  ...grant,
                                  currentVested: grant.optionsVested,
                                  availableToAccelerate: Math.max(
                                    0,
                                    grant.optionsGranted - grant.optionsVested
                                  ),
                                  grantId: grant.id,
                                  numberOfOptionsToBeAccelerated:
                                    existingEntry?.numberOfOptionsToBeAccelerated ||
                                    0,
                                  accelerateDate:
                                    existingEntry?.accelerateDate || "",
                                };

                                setLocalAccelerateDialog({
                                  open: true,
                                  grant: grantWithStoreData,
                                });
                              }}
                            />
                            <Dialog
                              open={localAccelerateDialog.open}
                              maxWidth="md"
                            >
                              <div className="w-[900px]">
                                {localAccelerateDialog.grant && (
                                  <AccelerateVesting
                                    onClose={() => {
                                      setLocalAccelerateDialog({
                                        open: false,
                                      });
                                      getResignationDetails();
                                    }}
                                    grant={localAccelerateDialog.grant}
                                    saveOnly={true}
                                    onSave={handleSaveAccelerateEntry}
                                  />
                                )}
                              </div>
                            </Dialog>
                            <Notes
                              className="w-full"
                              text="Note: Click 'Edit' to set the number of options to accelerate and the new vesting date for a grant. Your changes are saved locally — click 'Accelerate All' to submit and apply all saved entries. Grants with paused vesting or with no available options cannot be accelerated."
                            />
                          </>
                        )}
                      </VStack>
                    </CollapsibleComponent>
                  )}
                  {totalVested - totalExercised > 0 && (
                    <CollapsibleComponent
                      label={
                        openField !== "Exercise"
                          ? `Would you like to exercise options for ${employeeName} ?`
                          : "Exercise Options"
                      }
                      labelDetail={``}
                      isOpen={openField !== "Exercise"}
                      onClick={() =>
                        openField !== "Exercise"
                          ? setOpenField("Exercise")
                          : setOpenField("")
                      }
                    >
                      <VStack className="gap-4">
                        <VStack className="">
                          <p className="font-medium text-md">
                            Would you like to exercise options for{" "}
                            {employeeName} ?
                          </p>
                          <p>
                            Exercise options would mean that options will be
                            exercised for this employee by the company
                          </p>
                        </VStack>
                        <HStack className="gap-40">
                          <HStack className="items-center gap-2">
                            <input
                              type="radio"
                              className={`accent-orange-501 outline-hidden mr-1
                            ${
                              resignationDetails?.exerciseOptionsDetails
                                ?.allowExercise
                                ? "cursor-not-allowed"
                                : "cursor-pointer"
                            }`}
                              disabled={
                                resignationDetails?.exerciseOptionsDetails
                                  ?.allowExercise
                              }
                              checked={enableExercise}
                              onChange={(e) =>
                                setEnableExercise(e.target.checked)
                              }
                            />
                            <Label className="mt-2">Yes</Label>
                          </HStack>
                          <HStack className="items-center gap-2">
                            <input
                              type="radio"
                              className={`accent-orange-501 outline-hidden mr-1
                  ${
                    resignationDetails?.exerciseOptionsDetails?.allowExercise
                      ? "cursor-not-allowed"
                      : "cursor-pointer"
                  }`}
                              disabled={
                                resignationDetails?.exerciseOptionsDetails
                                  ?.allowExercise
                              }
                              checked={!enableExercise}
                              onChange={(e) =>
                                setEnableExercise(!e.target.checked)
                              }
                            />
                            <Label className="mt-2">No</Label>
                          </HStack>
                        </HStack>
                        {enableExercise && (
                          <VStack>
                            {!resignationDetails?.exerciseOptionsDetails
                              ?.allowExercise && (
                              <>
                                <HStack className="justify-between">
                                  <VStack>
                                    <Label>No. Of Options</Label>
                                    <Input
                                      type="number"
                                      className={`${
                                        resignationDetails
                                          ?.exerciseOptionsDetails
                                          ?.allowExercise
                                          ? "cursor-not-allowed"
                                          : ""
                                      }`}
                                      disabled={
                                        resignationDetails
                                          ?.exerciseOptionsDetails
                                          ?.allowExercise
                                      }
                                      {...formik.getFieldProps(
                                        "exerciseOptionsDetails.totalOptionsToExercise"
                                      )}
                                    />
                                  </VStack>
                                  <VStack>
                                    <Label>Exercise Date</Label>
                                    <Input
                                      type="date"
                                      className={`${
                                        resignationDetails
                                          ?.exerciseOptionsDetails
                                          ?.allowExercise
                                          ? "cursor-not-allowed"
                                          : ""
                                      }`}
                                      disabled={
                                        resignationDetails
                                          ?.exerciseOptionsDetails
                                          ?.allowExercise
                                      }
                                      {...formik.getFieldProps(
                                        "exerciseOptionsDetails.exerciseDate"
                                      )}
                                    />
                                  </VStack>
                                  <VStack>
                                    <Label>Fair Market Value</Label>
                                    <Input
                                      type="number"
                                      className={`${
                                        resignationDetails
                                          ?.exerciseOptionsDetails
                                          ?.allowExercise
                                          ? "cursor-not-allowed"
                                          : ""
                                      }`}
                                      disabled={
                                        resignationDetails
                                          ?.exerciseOptionsDetails
                                          ?.allowExercise
                                      }
                                      {...formik.getFieldProps(
                                        "exerciseOptionsDetails.fairMarketValue"
                                      )}
                                    />
                                  </VStack>
                                  <VStack>
                                    <Label>Exercise Type</Label>
                                    <Select
                                      className={`${
                                        resignationDetails
                                          ?.exerciseOptionsDetails
                                          ?.allowExercise
                                          ? "cursor-not-allowed"
                                          : ""
                                      }`}
                                      isPlaceHolderDisabled={false}
                                      options={["CASH", "STOCK"]}
                                      disabled={
                                        resignationDetails
                                          ?.exerciseOptionsDetails
                                          ?.allowExercise
                                      }
                                      {...formik.getFieldProps(
                                        "exerciseOptionsDetails.exerciseType"
                                      )}
                                    />
                                  </VStack>
                                </HStack>
                                <HStack className="justify-end mt-4">
                                  <ButtonPrimary1
                                    className={`w-fit`}
                                    disabled={
                                      resignationDetails?.exerciseOptionsDetails
                                        ?.allowExercise
                                    }
                                    onClick={() => {
                                      const resignationDetails =
                                        getDefaultResignationProcessObject(_id);
                                      resignationDetails.initiationDetails =
                                        formik.values.initiationDetails;
                                      resignationDetails.exerciseOptionsDetails =
                                        formik.values.exerciseOptionsDetails;
                                      resignationDetails.exerciseOptionsDetails.allowExercise =
                                        true;
                                      resignationDetails.exerciseOptionsDetails.getPreview =
                                        true;
                                      resignationProcess(resignationDetails, {
                                        onSuccess: (data) => {
                                          formik.setFieldValue(
                                            "exerciseOptionsDetails",
                                            data.data.exerciseOptionsDetails
                                          );
                                        },
                                        onError: (err: any) => {
                                          const errMsg =
                                            err.response?.data?.errorsValidation
                                              ?.errorRaw;
                                          if (errMsg) {
                                            errorMessage.setMessage(errMsg);
                                            toast(errMsg, {
                                              type: "error",
                                              autoClose: 2000,
                                            });
                                          }
                                        },
                                      });
                                    }}
                                  >
                                    Preview
                                  </ButtonPrimary1>
                                </HStack>
                              </>
                            )}
                            {_.sumBy(
                              formik.values?.exerciseOptionsDetails
                                .grantDetailsForExercises,
                              (e) => e.noOfOptionsToExercise
                            ) > 0 && (
                              <>
                                <ExerciseDetailsTable
                                  grantDetails={
                                    formik.values.exerciseOptionsDetails
                                      .grantDetailsForExercises
                                  }
                                />
                                {!resignationDetails?.exerciseOptionsDetails
                                  ?.allowExercise && (
                                  <HStack className="justify-end mt-10">
                                    <ButtonPrimary1
                                      className={`w-fit ${
                                        resignationDetails
                                          ?.exerciseOptionsDetails
                                          ?.allowExercise
                                          ? "cursor-not-allowed"
                                          : "cursor-pointer"
                                      }`}
                                      disabled={
                                        resignationDetails
                                          ?.exerciseOptionsDetails
                                          ?.allowExercise
                                      }
                                      onClick={() => {
                                        handleExercise(formik.values);
                                      }}
                                    >
                                      Exercise
                                    </ButtonPrimary1>
                                  </HStack>
                                )}
                              </>
                            )}
                          </VStack>
                        )}
                      </VStack>
                    </CollapsibleComponent>
                  )}
                  <CollapsibleComponent
                    label={
                      openField !== "Settlement Letter"
                        ? `Would you like to generate the full and final Settlement letter for ${employeeName}?`
                        : "Full and Final Settlement Letter"
                    }
                    labelDetail={``}
                    isOpen={openField !== "Settlement Letter"}
                    onClick={() =>
                      openField !== "Settlement Letter"
                        ? setOpenField("Settlement Letter")
                        : setOpenField("")
                    }
                  >
                    <VStack className="gap-4">
                      <VStack className="">
                        <p className="font-medium text-md">
                          Would you like to generate the full and final
                          Settlement letter for {employeeName}?
                        </p>
                      </VStack>
                      <HStack className="gap-40">
                        <HStack className="items-center gap-2">
                          <input
                            type="radio"
                            className="mr-1 cursor-pointer accent-orange-501 outline-hidden"
                            checked={enableSettlementLetter}
                            onChange={(e) =>
                              setEnableSettlementLetter(e.target.checked)
                            }
                          />
                          <Label className="mt-2">Yes</Label>
                        </HStack>
                        <HStack className="items-center gap-2">
                          <input
                            type="radio"
                            className="mr-1 cursor-pointer accent-orange-501 outline-hidden"
                            checked={!enableSettlementLetter}
                            onChange={(e) =>
                              setEnableSettlementLetter(!e.target.checked)
                            }
                          />
                          <Label className="mt-2">No</Label>
                        </HStack>
                      </HStack>
                      {enableSettlementLetter &&
                        formik.values.fafLetterDetails.grantsStatus.length >
                          0 && (
                          <VStack>
                            <p>The Grant table of the Employee is as follows</p>
                            <SettlementDetailsTable
                              grantDetails={
                                formik.values.fafLetterDetails.grantsStatus
                              }
                              currencySymbol={currencySymbol}
                              currencyType={currencyType}
                            />
                            {/* Header row: icon + title + timestamp + action buttons */}
                            <HStack className="justify-between items-start mt-8">
                              <HStack className="items-center gap-2">
                                <img className="h-8" src={pdfLogo} alt="PDF" />
                                <VStack className="gap-0">
                                  <p
                                    className={`text-sm font-semibold text-gray-800 ${
                                      formik.values.fafLetterDetails
                                        .settlementLetterUrl
                                        ? "underline cursor-pointer"
                                        : ""
                                    }`}
                                    onClick={() => {
                                      if (
                                        formik.values.fafLetterDetails
                                          .settlementLetterUrl
                                      ) {
                                        setDialog({
                                          open: true,
                                          mode: "View Settlement Letter",
                                          data: formik.values.fafLetterDetails
                                            .settlementLetterUrl,
                                        });
                                      }
                                    }}
                                  >
                                    F&amp;F Letter for {employeeName}
                                  </p>
                                  {formik.values.fafLetterDetails
                                    .settlementLetterUrl && (
                                    <p className="text-xs text-gray-400">
                                      Updated at{" "}
                                      {format(
                                        new Date(
                                          resignationDetails.fafLetterDetails.generationTime
                                        ),
                                        "dd-MMM-yyyy hh:mm aaaaa'm'"
                                      )}
                                    </p>
                                  )}
                                </VStack>
                              </HStack>
                              <HStack className="gap-3">
                                <ButtonPrimary1
                                  onClick={() => {
                                    setLoadingButton("generate");
                                    setIsFafSigned(false);
                                    setFafEmailWasSent(false);
                                    setFafEmailSentTo("");
                                    handleGenerateSettlementLetter(
                                      resignationDetails
                                    );
                                  }}
                                  loading={loadingButton === "generate"}
                                >
                                  <Icon
                                    icon="material-symbols:refresh"
                                    width={16}
                                    className="mr-1"
                                  />
                                  {formik.values.fafLetterDetails
                                    .settlementLetterUrl
                                    ? "Re-Generate"
                                    : "Generate"}
                                </ButtonPrimary1>
                                <ButtonPrimary1
                                  onClick={() => setFafUploadDialogOpen(true)}
                                >
                                  <Icon
                                    icon="material-symbols:upload"
                                    width={16}
                                    className="mr-1"
                                  />
                                  Upload
                                </ButtonPrimary1>
                                {formik.values.fafLetterDetails
                                  .settlementLetterUrl && (
                                  <ButtonPrimary1
                                    onClick={() => {
                                      downloadS3File(
                                        formik.values.fafLetterDetails
                                          .settlementLetterUrl,
                                        `F&F Letter for ${employeeName}.pdf`,
                                        false
                                      );
                                    }}
                                  >
                                    <Icon
                                      icon="material-symbols:download"
                                      width={24}
                                      color="#E85936"
                                    />
                                  </ButtonPrimary1>
                                )}
                              </HStack>
                            </HStack>

                            {/* Sign & Email section */}
                            {formik.values.fafLetterDetails
                              .settlementLetterUrl && (
                              <VStack className="gap-0 mt-4 border border-gray-200 rounded-md overflow-hidden divide-y divide-gray-200">
                                {resignationDetails.fafLetterDetails
                                  .isUserUploaded ? (
                                  /* Uploaded letter: single row */
                                  <HStack className="justify-between items-center p-4">
                                    <HStack className="items-center gap-3">
                                      <div
                                        className="w-8 h-8 rounded-full flex items-center justify-center"
                                        style={{ background: "#FEF2ED" }}
                                      >
                                        <Icon
                                          icon="material-symbols:upload-file"
                                          width={20}
                                          style={{ color: "#E85936" }}
                                        />
                                      </div>
                                      <VStack className="gap-0">
                                        <p className="text-sm font-medium text-gray-800">
                                          Letter Upload
                                        </p>
                                        <p className="text-xs text-gray-400">
                                          {format(
                                            new Date(
                                              resignationDetails
                                                .fafLetterDetails.updatedTime ||
                                                resignationDetails
                                                  .fafLetterDetails
                                                  .generationTime
                                            ),
                                            "dd-MMM-yyyy hh:mm aaaaa'm'"
                                          )}
                                        </p>
                                      </VStack>
                                    </HStack>
                                    <span
                                      className="px-2 py-0.5 text-xs font-medium rounded-full"
                                      style={{
                                        color: "#E85936",
                                        background: "#FEF2ED",
                                        border: "1px solid #E85936",
                                      }}
                                    >
                                      Completed
                                    </span>
                                  </HStack>
                                ) : (
                                  <>
                                    {/* Row 1: Letter Generated */}
                                    <HStack className="justify-between items-center p-4">
                                      <HStack className="items-center gap-3">
                                        <div
                                          className="w-8 h-8 rounded-full flex items-center justify-center"
                                          style={{ background: "#FEF2ED" }}
                                        >
                                          <Icon
                                            icon="material-symbols:check-circle"
                                            width={20}
                                            style={{ color: "#E85936" }}
                                          />
                                        </div>
                                        <VStack className="gap-0">
                                          <p className="text-sm font-medium text-gray-800">
                                            Letter Generation
                                          </p>
                                          <p className="text-xs text-gray-400">
                                            {format(
                                              new Date(
                                                resignationDetails.fafLetterDetails.generationTime
                                              ),
                                              "dd-MMM-yyyy hh:mm aaaaa'm'"
                                            )}
                                          </p>
                                        </VStack>
                                      </HStack>
                                      <span
                                        className="px-2 py-0.5 text-xs font-medium rounded-full"
                                        style={{
                                          color: "#E85936",
                                          background: "#FEF2ED",
                                          border: "1px solid #E85936",
                                        }}
                                      >
                                        Completed
                                      </span>
                                    </HStack>
                                    {!isFafSigned ? (
                                      <HStack className="items-start gap-3 p-4">
                                        <HStack className="items-center">
                                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                                            <Icon
                                              icon="material-symbols:draw-outline"
                                              className="text-gray-500"
                                              width={20}
                                            />
                                          </div>
                                        </HStack>
                                        <VStack className="gap-4 w-full">
                                          <HStack className="items-center gap-3">
                                            <p className="text-sm font-medium text-gray-800">
                                              Sign &amp; Send
                                            </p>
                                          </HStack>
                                          <VStack className="items-end gap-4 w-full">
                                            <div className="w-full rounded-lg bg-[#F5F8FA] border border-[#E8ECF3] py-3 px-4">
                                              <HStack className="items-center justify-between gap-4 w-full">
                                                <VStack className="gap-0">
                                                  <div className="font-medium text-gray-800">
                                                    Send email to employees
                                                  </div>
                                                  <div className="text-gray-500 text-xs">
                                                    Notify the employee about
                                                    this settlement letter
                                                  </div>
                                                </VStack>
                                                <div className="flex items-center shrink-0">
                                                  <SwitchButton
                                                    value={sendEmailAfterSign}
                                                    label=""
                                                    onClick={() => {
                                                      hasManualFafEmailOverride.current =
                                                        true;
                                                      setSendEmailAfterSign(
                                                        !sendEmailAfterSign
                                                      );
                                                    }}
                                                  />
                                                </div>
                                              </HStack>
                                            </div>
                                          </VStack>
                                          <ButtonPrimary
                                            onClick={handleFafAdminSign}
                                            loading={fafSignLoading}
                                            className="self-end"
                                          >
                                            {sendEmailAfterSign
                                              ? "Sign & Send Email"
                                              : "Sign Letter"}
                                          </ButtonPrimary>
                                        </VStack>
                                      </HStack>
                                    ) : (
                                      <>
                                        <HStack className="justify-between items-center p-4">
                                          <HStack className="items-center gap-3">
                                            <div
                                              className="w-8 h-8 rounded-full flex items-center justify-center"
                                              style={{ background: "#FEF2ED" }}
                                            >
                                              <Icon
                                                icon="material-symbols:check-circle"
                                                width={20}
                                                style={{ color: "#E85936" }}
                                              />
                                            </div>
                                            <VStack className="gap-0">
                                              <p className="text-sm font-medium text-gray-800">
                                                Admin Signature
                                              </p>
                                              {fafSignedAt && (
                                                <p className="text-xs text-gray-400">
                                                  Signed on{" "}
                                                  {format(
                                                    fafSignedAt,
                                                    "dd-MMM-yyyy h:mm aaaaa'm'"
                                                  )}
                                                </p>
                                              )}
                                            </VStack>
                                          </HStack>
                                          <span
                                            className="px-2 py-0.5 text-xs font-medium rounded-full"
                                            style={{
                                              color: "#E85936",
                                              background: "#FEF2ED",
                                              border: "1px solid #E85936",
                                            }}
                                          >
                                            Signed
                                          </span>
                                        </HStack>
                                        <HStack className="justify-between items-center p-4">
                                          <HStack className="items-center gap-3">
                                            <div
                                              className="w-8 h-8 rounded-full flex items-center justify-center"
                                              style={{
                                                background: fafEmailWasSent
                                                  ? "#FEF2ED"
                                                  : "#F3F4F6",
                                              }}
                                            >
                                              <Icon
                                                icon={
                                                  fafEmailWasSent
                                                    ? "material-symbols:mail"
                                                    : "material-symbols:mail-outline"
                                                }
                                                width={20}
                                                style={{
                                                  color: fafEmailWasSent
                                                    ? "#E85936"
                                                    : "#9CA3AF",
                                                }}
                                              />
                                            </div>
                                            <VStack className="gap-0">
                                              <p className="text-sm font-medium text-gray-800">
                                                Email Notification
                                              </p>
                                              <p className="text-xs text-gray-400">
                                                {fafEmailWasSent
                                                  ? `Sent to ${fafEmailSentTo}`
                                                  : "Skipped by admin"}
                                              </p>
                                            </VStack>
                                          </HStack>
                                          {fafEmailWasSent ? (
                                            <span
                                              className="px-2 py-0.5 text-xs font-medium rounded-full"
                                              style={{
                                                color: "#E85936",
                                                background: "#FEF2ED",
                                                border: "1px solid #E85936",
                                              }}
                                            >
                                              Sent
                                            </span>
                                          ) : (
                                            <span className="px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-full">
                                              Skipped
                                            </span>
                                          )}
                                        </HStack>
                                        <HStack className="justify-between items-center p-4">
                                          <HStack className="items-center gap-3">
                                            <div
                                              className="w-8 h-8 rounded-full flex items-center justify-center"
                                              style={{
                                                background: resignationDetails
                                                  ?.fafLetterDetails
                                                  ?.fafMetaData
                                                  ?.isEmployeeSigned
                                                  ? "#FEF2ED"
                                                  : "#F3F4F6",
                                              }}
                                            >
                                              <Icon
                                                icon={
                                                  resignationDetails
                                                    ?.fafLetterDetails
                                                    ?.fafMetaData
                                                    ?.isEmployeeSigned
                                                    ? "material-symbols:person"
                                                    : "material-symbols:person-outline"
                                                }
                                                width={20}
                                                style={{
                                                  color: resignationDetails
                                                    ?.fafLetterDetails
                                                    ?.fafMetaData
                                                    ?.isEmployeeSigned
                                                    ? "#E85936"
                                                    : "#9CA3AF",
                                                }}
                                              />
                                            </div>
                                            <VStack className="gap-0">
                                              <p className="text-sm font-medium text-gray-800">
                                                Employee Signature
                                              </p>
                                              <p className="text-xs text-gray-400">
                                                {resignationDetails
                                                  ?.fafLetterDetails
                                                  ?.fafMetaData
                                                  ?.isEmployeeSigned
                                                  ? "Signed by employee"
                                                  : "Awaiting employee signature"}
                                              </p>
                                            </VStack>
                                          </HStack>
                                          {resignationDetails?.fafLetterDetails
                                            ?.fafMetaData?.isEmployeeSigned ? (
                                            <span
                                              className="px-2 py-0.5 text-xs font-medium rounded-full"
                                              style={{
                                                color: "#E85936",
                                                background: "#FEF2ED",
                                                border: "1px solid #E85936",
                                              }}
                                            >
                                              Signed
                                            </span>
                                          ) : (
                                            <span className="px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-full">
                                              Pending
                                            </span>
                                          )}
                                        </HStack>
                                      </>
                                    )}
                                  </>
                                )}
                              </VStack>
                            )}
                          </VStack>
                        )}
                    </VStack>
                  </CollapsibleComponent>

                  <Dialog open={updateLWDDialog.open} maxWidth="md">
                    <div className="w-[900px]">
                      {resignationDetails && vestingData && (
                        <UpdateLastWorkingDateDialog
                          onClose={() => {
                            setUpdateLWDDialog({
                              open: false,
                            });
                          }}
                          currentLastWorkingDate={
                            formik.values.lastWorkingDayDetails.lastWorkingDate
                          }
                          vestingData={vestingData ?? []}
                          grantsData={
                            resignationDetails.initiationDetails
                              .grantTableDetails ?? []
                          }
                          separationReason={
                            resignationDetails.initiationDetails
                              .resignationReason
                          }
                          onSubmit={(lastWorkingDate: string) => {
                            formik.setFieldValue(
                              "lastWorkingDayDetails.lastWorkingDate",
                              lastWorkingDate
                            );
                            formik.setFieldValue(
                              "lastWorkingDayDetails.setLastWorkingDay",
                              true
                            );
                            handleLWD(
                              {
                                ...formik.values,
                                lastWorkingDayDetails: {
                                  ...formik.values.lastWorkingDayDetails,
                                  lastWorkingDate,
                                },
                              },
                              false
                            );
                            setUpdateLWDDialog({
                              open: false,
                            });
                          }}
                        />
                      )}
                    </div>
                  </Dialog>

                  <Box className="p-4 rounded-sm shadow-lg">
                    <Box className="flex flex-row items-center justify-start space-x-2 align-middle">
                      <Box className="text-sm font-medium text-black">
                        Complete Resignation
                      </Box>
                    </Box>
                    <VStack className="gap-4 p-4">
                      <VStack className="gap-3">
                        <p className="font-medium text-md">
                          Please check the information below and complete the
                          resignation of the Employee
                        </p>
                        {!resignationDetails?.lastWorkingDayDetails
                          ?.setLastWorkingDay && (
                          <div className="italic text-gray-500 w-fit">
                            * Note: Resignation Date (
                            {new Date(
                              resignationDetails.initiationDetails.resignationDate
                            )
                              .toUTCString()
                              .slice(5, 17)}
                            ) will be considered as Last Working Date
                          </div>
                        )}
                      </VStack>
                      <CompleteResignationGrantsTable
                        grantStatus={formik.values.finalGrantsStatus}
                        currencySymbol={currencySymbol}
                        currencyType={currencyType}
                      />

                      {dateForLapsing === "lastWorkingDay" &&
                        !resignationDetails?.lastWorkingDayDetails
                          ?.setLastWorkingDay && (
                          <div className="flex items-start gap-2 mt-3 p-3 bg-amber-50 border border-amber-300 rounded-md text-sm text-amber-800">
                            <Icon
                              icon="material-symbols:warning-outline-rounded"
                              className="w-5 h-5 shrink-0 mt-0.5 text-amber-500"
                            />
                            <span>
                              <strong>Last Working Day is required</strong> to
                              complete this resignation. Your company is
                              configured to use the Last Working Day as the
                              lapsing date for unvested options. Please set the
                              Last Working Day above before proceeding.
                            </span>
                          </div>
                        )}
                      {resignationDetails?.initiationDetails
                        ?.deleteWithdrawnGrants === false &&
                        withdrawnGrants.length > 0 && (
                          <VStack className="gap-3 mt-4">
                            <p className="text-sm text-gray-600">
                              Withdrawn grants will not be deleted. All options
                              in these grants will be lapsed on completion.
                            </p>
                            <table className="w-full text-sm border border-collapse border-gray-300 table-auto">
                              <thead>
                                <tr className="bg-gray-100">
                                  <th className="px-4 py-2 text-left border border-gray-300">
                                    Grant Info
                                  </th>
                                  <th className="px-4 py-2 text-left border border-gray-300">
                                    Grant Date
                                  </th>
                                  <th className="px-4 py-2 text-left border border-gray-300">
                                    Effect on Grant
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {withdrawnGrants.map((grant: any) => (
                                  <tr
                                    key={grant.grantIdentifier}
                                    className="hover:bg-gray-50"
                                  >
                                    <td className="px-4 py-2 border border-gray-300">
                                      {grant.grantIdentifier}
                                    </td>
                                    <td className="px-4 py-2 border border-gray-300">
                                      {formatDisplayDate(
                                        grant.grantDate || new Date()
                                      )}
                                    </td>
                                    <td className="px-4 py-2 border border-gray-300">
                                      Options will be lapsed on completion
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </VStack>
                        )}
                    </VStack>
                  </Box>
                </VStack>
                <HStack className="justify-between">
                  <div></div>
                  <HStack>
                    {!resignationDetails?.pauseAndRevertOnResignationDate &&
                    !formik.values.lastWorkingDayDetails.lastWorkingDate ? (
                      <Tooltip
                        text={
                          "Last Working Day is required to complete resignation"
                        }
                      >
                        <div>
                          <ButtonPrimary
                            type="submit"
                            className="ml-3"
                            disabled={
                              isFutureResignation ||
                              (!resignationDetails?.pauseAndRevertOnResignationDate &&
                                !formik.values.lastWorkingDayDetails
                                  .lastWorkingDate)
                            }
                          >
                            Complete
                          </ButtonPrimary>
                        </div>
                      </Tooltip>
                    ) : (
                      <div>
                        <ButtonPrimary
                          type="submit"
                          className="ml-3"
                          disabled={
                            isFutureResignation ||
                            (!resignationDetails?.pauseAndRevertOnResignationDate &&
                              !formik.values.lastWorkingDayDetails
                                .lastWorkingDate)
                          }
                        >
                          Complete
                        </ButtonPrimary>
                      </div>
                    )}
                  </HStack>
                </HStack>
              </VStack>
            </Form>
          )}
        </Formik>
      )}
      {isResignationComplete && resignationDetails && (
        <VStack className="gap-8 p-4">
          <Dialog
            open={dialog.open}
            onClose={() => {
              setDialog({
                open: false,
              });
            }}
            maxWidth="md"
          >
            {dialog.mode === "View Settlement Letter" && dialog.data && (
              <LazyPdfDocument url={dialog.data as string} />
            )}
          </Dialog>
          <HStack className="gap-12">
            <span className="text-sm font-normal whitespace-nowrap">
              Resignation Date
            </span>
            <span className="text-sm font-medium text-gray-600">
              {employee.resignationDate
                ? new Date(employee.resignationDate).toUTCString().slice(5, 17)
                : "-"}
            </span>
            <span className="text-sm font-normal ml-36 whitespace-nowrap">
              Last Working Day
            </span>
            <span className="text-sm font-medium text-gray-600">
              {employee.lastDay
                ? new Date(employee.lastDay || "").toUTCString().slice(5, 17)
                : "-"}
            </span>
          </HStack>
          {employee.isPausedVesting && (
            <HStack>
              <span className="text-sm font-normal whitespace-nowrap">
                The vesting has been paused on{" "}
                <span className="text-sm font-medium text-gray-600">
                  {resignationDetails?.pauseVestingDetails.pauseVestingDate
                    ? new Date(
                        resignationDetails?.pauseVestingDetails.pauseVestingDate
                      )
                        .toUTCString()
                        .slice(5, 17)
                    : "-"}
                </span>
              </span>
            </HStack>
          )}
          {resignationDetails.finalGrantsStatus.length > 0 && (
            <ResignationSummaryTable
              grantStatus={resignationDetails.finalGrantsStatus}
              currencySymbol={currencySymbol}
              currencyType={currencyType}
            />
          )}
          <div className="mt-2 gap-4">
            {(() => {
              const extendableGrants =
                resignationDetails.finalGrantsStatus.filter(
                  (g) => g.optionsAvailableForExercise > 0
                );
              if (extendableGrants.length === 0) return null;
              return (
                <>
                  <div className="w-full flex items-center justify-between px-4 py-3.5 rounded-lg bg-[#F5F8FA] border border-[#E8ECF3]">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 w-[3px] self-stretch rounded-full bg-primary shrink-0" />
                      <div>
                        <div className="text-xs font-semibold text-primary">
                          Extend Exercise
                        </div>
                        <div className="text-xs text-[#737385] mt-0.5">
                          Give employees more time to exercise vested options
                          before they expire across {extendableGrants.length}{" "}
                          grants.
                        </div>
                      </div>
                    </div>
                    <div className="ml-4 shrink-0">
                      <ButtonPrimary1
                        onClick={() => {
                          setExtendExerciseDlgOpen(true);
                        }}
                      >
                        Extend exercise
                      </ButtonPrimary1>
                    </div>
                  </div>
                  <ExtendExerciseDialog
                    open={extendExerciseDlgOpen}
                    onClose={() => setExtendExerciseDlgOpen(false)}
                    grantIds={extendableGrants.map((g) => g.id)}
                    employeeInfo={{ name: employeeName }}
                    onSuccess={() => {
                      setExtendExerciseDlgOpen(false);
                      getResignationDetails();
                    }}
                  />
                </>
              );
            })()}
            {/* Two flat action panels for completed resignation view */}

            {showForfeitPanel && (
              <div className="w-full flex items-center justify-between px-4 py-3.5 mt-4 rounded-lg bg-[#F5F8FA] border border-[#E8ECF3]">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 w-[3px] self-stretch rounded-full bg-primary shrink-0" />
                  <div>
                    <div className="text-xs font-semibold text-primary">
                      Forfeit
                    </div>
                    <div className="text-xs text-[#737385] mt-0.5">
                      Forfeit expired options immediately across{" "}
                      {
                        resignationDetails.finalGrantsStatus.filter(
                          (g) => new Date(g.newExpiryDate) < new Date()
                        ).length
                      }{" "}
                      grant(s). This action cannot be undone.
                    </div>
                  </div>
                </div>
                <div className="ml-4 shrink-0">
                  <ButtonPrimary1
                    onClick={() => setForfeitConfirmOpen(true)}
                    className="inline-flex items-center px-3 py-1.5 rounded-sm border border-primary text-xs font-semibold text-primary bg-transparent hover:bg-primary/10 focus:outline-none whitespace-nowrap cursor-pointer"
                  >
                    Forfeit now
                  </ButtonPrimary1>
                </div>
                <AlertDialog
                  message={
                    "Are you sure you want to immediately forfeit expired options for this employee? This action cannot be undone."
                  }
                  error={false}
                  status={false}
                  primaryActionText="Confirm"
                  secondaryActionText="Cancel"
                  onPrimaryAction={async () => {
                    try {
                      setForfeitLoading(true);
                      await manualForfeiture([_id]);
                      setForfeitConfirmOpen(false);
                      toast("Forfeiture executed", {
                        type: "success",
                      });
                      getResignationDetails();
                    } catch (err: any) {
                      const errMsg =
                        err?.response?.data?.reason ||
                        err?.message ||
                        "Failed to run forfeiture";
                      errorMessage.setMessage(errMsg);
                      toast(errMsg, { type: "error" });
                    } finally {
                      setForfeitLoading(false);
                    }
                  }}
                  onSecondaryAction={() => setForfeitConfirmOpen(false)}
                  open={forfeitConfirmOpen}
                  loading={forfeitLoading}
                />
              </div>
            )}
          </div>
          <Box className="p-4 rounded-md shadow-lg py-5">
            {/* Header row: icon + title + timestamp + action buttons */}
            <HStack className="justify-between items-start mb-2">
              <HStack className="items-center gap-2">
                <img className="h-8" src={pdfLogo} alt="PDF" />
                <VStack className="gap-0">
                  <p
                    className="text-sm font-semibold text-gray-800 underline cursor-pointer"
                    onClick={() => {
                      if (
                        resignationDetails.fafLetterDetails.settlementLetterUrl
                      ) {
                        setDialog({
                          open: true,
                          mode: "View Settlement Letter",
                          data: resignationDetails.fafLetterDetails
                            .settlementLetterUrl,
                        });
                      }
                    }}
                  >
                    F&amp;F Letter for {employeeName}
                  </p>
                  {resignationDetails.fafLetterDetails.settlementLetterUrl && (
                    <p className="text-xs text-gray-400">
                      Updated at{" "}
                      {format(
                        new Date(
                          resignationDetails.fafLetterDetails.generationTime
                        ),
                        "dd-MMM-yyyy hh:mm aaaaa'm'"
                      )}
                    </p>
                  )}
                </VStack>
              </HStack>
              <HStack className="gap-3">
                <ButtonPrimary1
                  onClick={() => {
                    setLoadingButton("generate");
                    setIsFafSigned(false);
                    setFafEmailWasSent(false);
                    setFafEmailSentTo("");
                    handleGenerateSettlementLetter(resignationDetails);
                  }}
                  loading={loadingButton === "generate"}
                >
                  <Icon
                    icon="material-symbols:refresh"
                    width={16}
                    className="mr-1"
                  />
                  Re-Generate
                </ButtonPrimary1>
                <ButtonPrimary1 onClick={() => setFafUploadDialogOpen(true)}>
                  <Icon
                    icon="material-symbols:upload"
                    width={16}
                    className="mr-1"
                  />
                  Upload
                </ButtonPrimary1>
                {resignationDetails.fafLetterDetails.settlementLetterUrl && (
                  <ButtonPrimary1
                    onClick={() => {
                      downloadS3File(
                        resignationDetails.fafLetterDetails.settlementLetterUrl,
                        `F&F Letter for ${employeeName}.pdf`,
                        false
                      );
                    }}
                  >
                    <Icon
                      icon="material-symbols:download"
                      width={24}
                      color="#E85936"
                    />
                  </ButtonPrimary1>
                )}
              </HStack>
            </HStack>

            {/* Status rows */}
            {resignationDetails.fafLetterDetails.settlementLetterUrl && (
              <VStack className="border border-gray-200 rounded-md overflow-hidden divide-y divide-gray-200">
                {resignationDetails.fafLetterDetails.isUserUploaded ? (
                  /* Uploaded letter: single row */
                  <HStack className="justify-between items-center p-4">
                    <HStack className="items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{ background: "#FEF2ED" }}
                      >
                        <Icon
                          icon="material-symbols:upload-file"
                          width={20}
                          style={{ color: "#E85936" }}
                        />
                      </div>
                      <VStack className="gap-0">
                        <p className="text-sm font-medium text-gray-800">
                          Letter Uploaded
                        </p>
                        <p className="text-xs text-gray-400">
                          {format(
                            new Date(
                              resignationDetails.fafLetterDetails.updatedTime ||
                                resignationDetails.fafLetterDetails
                                  .generationTime
                            ),
                            "dd-MMM-yyyy hh:mm aaaaa'm'"
                          )}
                        </p>
                      </VStack>
                    </HStack>
                    <span
                      className="px-2 py-0.5 text-xs font-medium rounded-full"
                      style={{
                        color: "#E85936",
                        background: "#FEF2ED",
                        border: "1px solid #E85936",
                      }}
                    >
                      Completed
                    </span>
                  </HStack>
                ) : (
                  <>
                    {/* Row 1: Letter Generated */}
                    <HStack className="justify-between items-center p-4">
                      <HStack className="items-center gap-3">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center"
                          style={{ background: "#FEF2ED" }}
                        >
                          <Icon
                            icon="material-symbols:check-circle"
                            width={20}
                            style={{ color: "#E85936" }}
                          />
                        </div>
                        <VStack className="gap-0">
                          <p className="text-sm font-medium text-gray-800">
                            Letter Generated
                          </p>
                          <p className="text-xs text-gray-400">
                            {format(
                              new Date(
                                resignationDetails.fafLetterDetails.generationTime
                              ),
                              "dd-MMM-yyyy hh:mm aaaaa'm'"
                            )}
                          </p>
                        </VStack>
                      </HStack>
                      <span
                        className="px-2 py-0.5 text-xs font-medium rounded-full"
                        style={{
                          color: "#E85936",
                          background: "#FEF2ED",
                          border: "1px solid #E85936",
                        }}
                      >
                        Complete
                      </span>
                    </HStack>

                    {/* Row 2+: Sign & Send flow */}
                    {!isFafSigned ? (
                      <HStack className="items-start gap-3 p-4">
                        <HStack className="items-center">
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                            <Icon
                              icon="material-symbols:draw-outline"
                              className="text-gray-500"
                              width={20}
                            />
                          </div>
                        </HStack>
                        <VStack className="gap-4 w-full">
                          <HStack className="items-center gap-3">
                            <p className="text-sm font-medium text-gray-800">
                              Sign &amp; Send
                            </p>
                          </HStack>
                          <VStack className="items-end gap-4 w-full">
                            <div className="w-full rounded-lg bg-[#F5F8FA] border border-[#E8ECF3] py-3 px-4">
                              <HStack className="items-center justify-between gap-4 w-full">
                                <VStack className="gap-0">
                                  <div className="font-medium text-gray-800">
                                    Send email to employees
                                  </div>
                                  <div className="text-gray-500 text-xs">
                                    Notify the employee about this settlement
                                    letter
                                  </div>
                                </VStack>
                                <div className="flex items-center shrink-0">
                                  <SwitchButton
                                    value={sendEmailAfterSign}
                                    label=""
                                    onClick={() => {
                                      hasManualFafEmailOverride.current = true;
                                      setSendEmailAfterSign(
                                        !sendEmailAfterSign
                                      );
                                    }}
                                  />
                                </div>
                              </HStack>
                            </div>
                          </VStack>
                          <ButtonPrimary
                            onClick={handleFafAdminSign}
                            loading={fafSignLoading}
                            className="self-end"
                          >
                            {sendEmailAfterSign
                              ? "Sign & Send Email"
                              : "Sign Letter"}
                          </ButtonPrimary>
                        </VStack>
                      </HStack>
                    ) : (
                      <>
                        <HStack className="justify-between items-center p-4">
                          <HStack className="items-center gap-3">
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center"
                              style={{ background: "#FEF2ED" }}
                            >
                              <Icon
                                icon="material-symbols:check-circle"
                                width={20}
                                style={{ color: "#E85936" }}
                              />
                            </div>
                            <VStack className="gap-0">
                              <p className="text-sm font-medium text-gray-800">
                                Admin Signature
                              </p>
                              {fafSignedAt && (
                                <p className="text-xs text-gray-400">
                                  Signed on{" "}
                                  {format(
                                    fafSignedAt,
                                    "dd-MMM-yyyy h:mm aaaaa'm'"
                                  )}
                                </p>
                              )}
                            </VStack>
                          </HStack>
                          <span
                            className="px-2 py-0.5 text-xs font-medium rounded-full"
                            style={{
                              color: "#E85936",
                              background: "#FEF2ED",
                              border: "1px solid #E85936",
                            }}
                          >
                            Signed
                          </span>
                        </HStack>
                        <HStack className="justify-between items-center p-4">
                          <HStack className="items-center gap-3">
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center"
                              style={{
                                background: fafEmailWasSent
                                  ? "#FEF2ED"
                                  : "#F3F4F6",
                              }}
                            >
                              <Icon
                                icon={
                                  fafEmailWasSent
                                    ? "material-symbols:mail"
                                    : "material-symbols:mail-outline"
                                }
                                width={20}
                                style={{
                                  color: fafEmailWasSent
                                    ? "#E85936"
                                    : "#9CA3AF",
                                }}
                              />
                            </div>
                            <VStack className="gap-0">
                              <p className="text-sm font-medium text-gray-800">
                                Email Notification
                              </p>
                              <p className="text-xs text-gray-400">
                                {fafEmailWasSent
                                  ? `Sent to ${fafEmailSentTo}`
                                  : "Skipped by admin"}
                              </p>
                            </VStack>
                          </HStack>
                          {fafEmailWasSent ? (
                            <span
                              className="px-2 py-0.5 text-xs font-medium rounded-full"
                              style={{
                                color: "#E85936",
                                background: "#FEF2ED",
                                border: "1px solid #E85936",
                              }}
                            >
                              Sent
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-full">
                              Skipped
                            </span>
                          )}
                        </HStack>
                        <HStack className="justify-between items-center p-4">
                          <HStack className="items-center gap-3">
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center"
                              style={{
                                background: resignationDetails?.fafLetterDetails
                                  ?.fafMetaData?.isEmployeeSigned
                                  ? "#FEF2ED"
                                  : "#F3F4F6",
                              }}
                            >
                              <Icon
                                icon={
                                  resignationDetails?.fafLetterDetails
                                    ?.fafMetaData?.isEmployeeSigned
                                    ? "material-symbols:person"
                                    : "material-symbols:person-outline"
                                }
                                width={20}
                                style={{
                                  color: resignationDetails?.fafLetterDetails
                                    ?.fafMetaData?.isEmployeeSigned
                                    ? "#E85936"
                                    : "#9CA3AF",
                                }}
                              />
                            </div>
                            <VStack className="gap-0">
                              <p className="text-sm font-medium text-gray-800">
                                Employee Signature
                              </p>
                              <p className="text-xs text-gray-400">
                                {resignationDetails?.fafLetterDetails
                                  ?.fafMetaData?.isEmployeeSigned
                                  ? "Signed by employee"
                                  : "Awaiting employee signature"}
                              </p>
                            </VStack>
                          </HStack>
                          {resignationDetails?.fafLetterDetails?.fafMetaData
                            ?.isEmployeeSigned ? (
                            <span
                              className="px-2 py-0.5 text-xs font-medium rounded-full"
                              style={{
                                color: "#E85936",
                                background: "#FEF2ED",
                                border: "1px solid #E85936",
                              }}
                            >
                              Signed
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-full">
                              Pending
                            </span>
                          )}
                        </HStack>
                      </>
                    )}
                  </>
                )}
              </VStack>
            )}
          </Box>
        </VStack>
      )}
    </VStack>
  );
};

const GrantStatusTable = ({
  grantStatus,
  currencySymbol,
  currencyType,
}: {
  grantStatus: GrantStatusForResignation[];
  currencySymbol: string;
  currencyType: string;
}) => (
  <table className="w-full table-space">
    <thead className="text-xxs font-normal text-[#B8B7C9]">
      <tr className="border-b border-gray-200 border-dashed">
        <td className="px-2 py-2 align-top">GRANT INFO</td>
        <td className="px-2 py-2 align-top">OPTIONS GRANTED</td>
        <td className="px-2 py-2 align-top">VESTED</td>
        <td className="flex-wrap px-2 py-2 align-top">NEXT VESTING DATE</td>
        <td className="px-2 py-2 align-top">OPTIONS AVAILABLE FOR EXERCISE</td>
        <td className="px-2 py-2 align-top">NEXT EXPIRY DATE</td>
      </tr>
    </thead>
    <tbody className="text-[#4E4E4E]">
      {grantStatus.map((grant) => {
        const expiryStr = grant.newExpiryDate || grant.currentExpiryDate;
        const expiryDate = expiryStr ? new Date(expiryStr) : null;
        const showExpiry =
          expiryDate && startOfDay(expiryDate) >= startOfDay(new Date());
        return (
          <tr
            key={grant.identifier}
            className="border-b border-gray-200 border-dashed "
          >
            <td className="px-2 py-2 align-top ">
              <HStack>
                <div className="mr-2 whitespace-nowrap">{grant.identifier}</div>
              </HStack>
              <p className="text-xxs">
                {new Date(grant.grantDate).toUTCString().slice(5, 17)}
              </p>
            </td>
            <td className="px-2 py-2 align-top">
              <span className="font-medium">{grant.optionsGranted}</span>
              <p className="text-xxs">
                Price: {currencySymbol}
                {grant.strikePrice}
              </p>
            </td>
            <td className="px-2 py-2 align-top">
              <span className="font-medium">{grant.optionsVested}</span>
              <HStack>
                <p className="mr-1 text-xxs whitespace-nowrap">
                  Cashed Out: {grant.cashedOut}
                </p>
                <p className="mr-1 text-xxs whitespace-nowrap">
                  | Stock Exercised: {grant.stockExercised}
                </p>
                <p className="mr-1 text-xxs whitespace-nowrap">
                  | Forfeited: {grant.forfeited}
                </p>
              </HStack>
            </td>
            <td className="px-2 py-2 align-top">
              {grant.nextVestingDate
                ? new Date(grant.nextVestingDate).toUTCString().slice(5, 17)
                : "-"}
            </td>
            <td className="px-2 py-2 align-top">
              <span>{grant.optionsAvailableForExercise}</span>
            </td>
            <td className="px-2 py-2 align-top">
              {expiryDate ? format(expiryDate!, "dd MMM yyyy") : "-"}
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

const PauseVestingGrantTable = ({
  grantStatus,
  currencyType,
}: {
  grantStatus: GrantStatusForResignation[];
  currencyType: string;
}) => (
  <table className="w-full table-space">
    <thead className="text-xxs font-normal text-[#B8B7C9]">
      <tr className="border-b border-gray-200 border-dashed">
        <td className="px-2 py-2 align-top">GRANT INFO</td>
        <td className="px-2 py-2 align-top">NO. OF VESTING EVENTS LEFT</td>
        <td className="px-2 py-2 align-top">VESTING PAUSE DATE</td>
        <td className="flex-wrap px-2 py-2 align-top">PAUSED OPTIONS</td>
      </tr>
    </thead>
    <tbody className="text-[#4E4E4E]">
      {grantStatus.map((grant) => (
        <tr
          key={grant.identifier}
          className="border-b border-gray-200 border-dashed "
        >
          <td className="px-2 py-2 align-top ">
            <HStack>
              <div className="mr-2 whitespace-nowrap">{grant.identifier}</div>
            </HStack>
            <p className="text-xxs">
              {new Date(grant.grantDate).toUTCString().slice(5, 17)}
            </p>
          </td>
          <td className="px-2 py-2 align-top">
            <span className="font-medium">{grant.noOfVestingEventsLeft}</span>
          </td>
          <td className="px-2 py-2 align-top">
            <span className="font-medium">
              {grant.vestingPauseDate
                ? new Date(grant.vestingPauseDate).toUTCString().slice(5, 17)
                : ""}
            </span>
          </td>
          <td className="px-2 py-2 align-top">{grant.pausedOptions}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const ExerciseDetailsTable = ({
  grantDetails,
}: {
  grantDetails: GrantDetailsForExercise[];
}) => (
  <table className="w-full table-space">
    <thead className="text-xxs font-normal text-[#B8B7C9]">
      <tr className="border-b border-gray-200 border-dashed">
        <td className="px-2 py-2 align-top">GRANT INFO</td>
        <td className="px-2 py-2 align-top">EXERCISE TYPE</td>
        <td className="px-2 py-2 align-top">NO. OF OPTIONS</td>
        <td className="flex-wrap px-2 py-2 align-top">EXERCISE DATE</td>
        <td className="px-2 py-2 align-top">FAIR MARKET VALUE</td>
      </tr>
    </thead>
    <tbody className="text-[#4E4E4E]">
      {grantDetails.map((grant, index) => (
        <tr
          key={grant.identifier}
          className="border-b border-gray-200 border-dashed "
        >
          <td className="px-2 py-2 align-top ">
            <HStack>
              <div className="mr-2 whitespace-nowrap">{grant.identifier}</div>
            </HStack>
            <p className="text-xxs">
              {new Date(grant.grantDate).toUTCString().slice(5, 17)}
            </p>
          </td>
          <td className="px-2 py-2 align-top">
            <VStack>{grant.exerciseType}</VStack>
          </td>
          <td className="px-2 py-2 align-top">
            <VStack>{grant.noOfOptionsToExercise}</VStack>
          </td>
          <td className="px-2 py-2 align-top">
            {grant.date ? new Date(grant.date).toUTCString().slice(5, 17) : ""}
          </td>
          <td className="px-2 py-2 align-top">
            <VStack>{grant.fairMarketValue}</VStack>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const AccelerateVestingTable = ({
  rows,
  onEdit,
}: {
  rows: any[];
  // open edit dialog for a row
  onEdit: (index: number) => void;
}) => (
  <table className="w-full table-space">
    <thead className="text-xxs font-normal text-[#B8B7C9]">
      <tr className="border-b border-gray-200 border-dashed">
        <td className="px-2 py-2 align-top">GRANT INFO</td>
        <td className="px-2 py-2 align-top">OPTIONS GRANTED</td>
        <td className="px-2 py-2 align-top">VESTED</td>
        <td className="flex-wrap px-2 py-2 align-top">
          OPTIONS GETTING LAPSED
        </td>
        <td className="px-2 py-2 align-top">NO. TO ACCELERATE</td>
        <td className="px-2 py-2 align-top">NEW VESTING DATE</td>
        <td className="px-2 py-2 align-top">ACCELERATE</td>
      </tr>
    </thead>
    <tbody className="text-[#4E4E4E]">
      {rows.map((grant, index) => (
        <tr
          key={grant.identifier + index}
          className="border-b border-gray-200 border-dashed "
        >
          <td className="px-2 py-2 align-top ">
            <HStack>
              <div className="mr-2 whitespace-nowrap">{grant.identifier}</div>
            </HStack>
            <p className="text-xxs">
              {grant.grantDate
                ? new Date(grant.grantDate).toUTCString().slice(5, 17)
                : ""}
            </p>
          </td>
          <td className="px-2 py-2 align-top">
            <span className="font-medium">{grant.optionsGranted}</span>
          </td>
          <td className="px-2 py-2 align-top">
            <span className="font-medium">{grant.optionsVested}</span>
          </td>
          <td className="px-2 py-2 align-top">
            {grant.optionsGranted - grant.optionsVested > 0
              ? grant.optionsGranted - grant.optionsVested
              : 0}
          </td>
          <td className="px-2 py-2 align-top">
            <div className="text-sm font-medium">
              {grant.noOfOptionsToAccelerate ?? "-"}
            </div>
          </td>
          <td className="px-2 py-2 align-top">
            <div className="text-sm font-medium">
              {grant.accelerateDate
                ? new Date(grant.accelerateDate).toUTCString().slice(5, 17)
                : "-"}
            </div>
          </td>
          <td className="px-2 py-2 align-top">
            {(() => {
              const _g = grant as any;
              const available =
                _g.availableToAccelerate ??
                (_g.optionsGranted - _g.optionsVested > 0
                  ? _g.optionsGranted - _g.optionsVested
                  : 0);
              const paused = _g.isPausedVesting === true || false;
              const canEdit = available > 0 && !paused;
              return (
                <div>
                  <ButtonPrimary1
                    className={"w-fit"}
                    onClick={() => onEdit(index)}
                    disabled={!canEdit}
                  >
                    Edit
                  </ButtonPrimary1>
                  {!canEdit && (
                    <div className="text-xxs text-gray-500 mt-1">
                      {available <= 0 ? "No options available" : ""}
                    </div>
                  )}
                </div>
              );
            })()}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const SettlementDetailsTable = ({
  grantDetails,
  currencySymbol,
  currencyType,
}: {
  grantDetails: GrantDetailsForExtendExercise[];
  currencySymbol: string;
  currencyType: string;
}) => (
  <table className="w-full table-space">
    <thead className="text-xxs font-normal text-[#B8B7C9]">
      <tr className="border-b border-gray-200 border-dashed">
        <td className="px-2 py-2 align-top">GRANT INFO</td>
        <td className="px-2 py-2 align-top">OPTIONS GRANTED</td>
        <td className="px-2 py-2 align-top">VESTED</td>
        <td className="flex-wrap px-2 py-2 align-top">
          OPTIONS AVAILABLE FOR EXERCISE
        </td>
        <td className="flex-wrap px-2 py-2 align-top">
          OPTIONS PENDING FOR EXERCISE APPROVAL
        </td>
        <td className="px-2 py-2 align-top">PLAN EXERCISE EXPIRY DATE</td>
        <td className="px-2 py-2 align-top">NEW EXPIRY DATE</td>
      </tr>
    </thead>
    <tbody className="text-[#4E4E4E]">
      {grantDetails.map((grant, index) => (
        <tr
          key={grant.identifier}
          className="border-b border-gray-200 border-dashed "
        >
          <td className="px-2 py-2 align-top ">
            <HStack>
              <div className="mr-2 whitespace-nowrap">{grant.identifier}</div>
            </HStack>
            <p className="text-xxs">
              {new Date(grant.grantDate).toUTCString().slice(5, 17)}
            </p>
          </td>
          <td className="px-2 py-2 align-top">
            <VStack>
              <span className="font-medium">{grant.optionsGranted}</span>
              <p className="text-xxs">
                Price: {currencySymbol}
                {grant.strikePrice}
              </p>
            </VStack>
          </td>
          <td className="px-2 py-2 align-top">
            <VStack>
              <span className="font-medium">{grant.optionsVested}</span>
              <HStack>
                <p className="mr-1 text-xxs whitespace-nowrap">
                  Cashed Out: {grant.cashedOut}
                </p>
                <p className="mr-1 text-xxs whitespace-nowrap">
                  | Stock Exercised: {grant.stockExercised}
                </p>
                <p className="mr-1 text-xxs whitespace-nowrap">
                  | Forfeited: {grant.forfeited}
                </p>
              </HStack>
            </VStack>
          </td>
          <td className="px-2 py-2 align-top">
            {grant.optionsAvailableForExercise}
          </td>
          <td className="px-2 py-2 align-top">{grant.noOfOptionsToExercise}</td>
          <td className="px-2 py-2 align-top">{grant.noOfOptionsToExercise}</td>
          <td className="px-2 py-2 align-top">
            <VStack>
              {grant.currentExpiryDate
                ? format(
                    new Date(grant.newExpiryDate || grant.currentExpiryDate),
                    "dd MMM yyyy"
                  )
                : "-"}
            </VStack>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const CompleteResignationGrantsTable = ({
  grantStatus,
  currencySymbol,
  currencyType,
}: {
  grantStatus: GrantStatusForResignation[];
  currencySymbol: string;
  currencyType: string;
}) => (
  <table className="w-full table-space">
    <thead className="text-xxs font-normal text-[#B8B7C9]">
      <tr className="border-b border-gray-200 border-dashed">
        <td className="px-2 py-2 align-top">GRANT INFO</td>
        <td className="px-2 py-2 align-top">OPTIONS GRANTED</td>
        <td className="px-2 py-2 align-top">VESTED</td>
        <td className="px-2 py-2 align-top">PAUSED OPTIONS</td>
        <td className="flex-wrap px-2 py-2 align-top">NEXT EXPIRY DATE</td>
        <td className="px-2 py-2 align-top">OPTIONS TO BE EXERCISED</td>
      </tr>
    </thead>
    <tbody className="text-[#4E4E4E]">
      {grantStatus.map((grant) => {
        const expiryStr = grant.newExpiryDate || grant.currentExpiryDate;
        const expiryDate = expiryStr ? new Date(expiryStr) : null;
        const showExpiry =
          expiryDate && startOfDay(expiryDate) >= startOfDay(new Date());
        return (
          <tr
            key={grant.identifier}
            className="border-b border-gray-200 border-dashed "
          >
            <td className="px-2 py-2 align-top ">
              <HStack>
                <div className="mr-2 whitespace-nowrap">{grant.identifier}</div>
              </HStack>
              <p className="text-xxs">
                {new Date(grant.grantDate).toUTCString().slice(5, 17)}
              </p>
            </td>
            <td className="px-2 py-2 align-top">
              <span className="font-medium">{grant.optionsGranted}</span>
              <p className="text-xxs">
                Price: {currencySymbol}
                {grant.strikePrice}
              </p>
            </td>
            <td className="px-2 py-2 align-top">
              <span className="font-medium">{grant.optionsVested}</span>
              <HStack>
                <p className="mr-1 text-xxs whitespace-nowrap">
                  Cashed Out: {grant.cashedOut}
                </p>
                <p className="mr-1 text-xxs whitespace-nowrap">
                  | Stock Exercised: {grant.stockExercised}
                </p>
                <p className="mr-1 text-xxs whitespace-nowrap">
                  | Forfeited: {grant.forfeited}
                </p>
              </HStack>
            </td>
            <td className="px-2 py-2 align-top">{grant.pausedOptions}</td>
            <td className="px-2 py-2 align-top">
              {expiryDate ? format(expiryDate!, "dd MMM yyyy") : "-"}
            </td>
            <td className="px-2 py-2 align-top">
              <span>{grant.noOfOptionsToExercise}</span>
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

const ResignationSummaryTable = ({
  grantStatus,
  currencySymbol,
  currencyType,
}: {
  grantStatus: GrantStatusForResignation[];
  currencySymbol: string;
  currencyType: string;
}) => (
  <table className="w-full table-space">
    <thead className="text-xxs font-normal text-[#B8B7C9]">
      <tr className="border-b border-gray-200 border-dashed">
        <td className="px-2 py-2 align-top">GRANT INFO</td>
        <td className="px-2 py-2 align-top">OPTIONS GRANTED</td>
        <td className="px-2 py-2 align-top">OPTIONS VESTED</td>
        <td className="px-2 py-2 align-top">OPTIONS EXERCISED</td>
        <td className="flex-wrap px-2 py-2 align-top">NEXT EXPIRY DATE</td>
      </tr>
    </thead>
    <tbody className="text-[#4E4E4E]">
      {grantStatus.map((grant) => {
        const expiryStr = grant.newExpiryDate || grant.currentExpiryDate;
        const expiryDate = expiryStr ? new Date(expiryStr) : null;
        const showExpiry =
          expiryDate && startOfDay(expiryDate) >= startOfDay(new Date());
        return (
          <tr
            key={grant.identifier}
            className="border-b border-gray-200 border-dashed "
          >
            <td className="px-2 py-2 align-top ">
              <HStack>
                <div className="mr-2 whitespace-nowrap">{grant.identifier}</div>
              </HStack>
              <p className="text-xxs">
                {new Date(grant.grantDate).toUTCString().slice(5, 17)}
              </p>
            </td>
            <td className="px-2 py-2 align-top">
              <span className="font-medium">{grant.optionsGranted}</span>
              <p className="text-xxs">
                Price: {currencySymbol}
                {grant.strikePrice}
              </p>
            </td>
            <td className="px-2 py-2 align-top">
              <span className="font-medium">{grant.optionsVested}</span>
              <HStack>
                <p className="mr-1 text-xxs whitespace-nowrap">
                  Cashed Out: {grant.cashedOut}
                </p>
                <p className="mr-1 text-xxs whitespace-nowrap">
                  | Stock Exercised: {grant.stockExercised}
                </p>
                <p className="mr-1 text-xxs whitespace-nowrap">
                  | Forfeited: {grant.forfeited}
                </p>
              </HStack>
            </td>
            <td className="px-2 py-2 align-top">
              {grant.optionsExercisedDuringResignation.toLocaleString(
                currencyType
              )}
            </td>
            <td className="px-2 py-2 align-top">
              {expiryDate ? format(expiryDate!, "dd MMM yyyy") : "-"}
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

export const getDefaultResignationProcessObject = (employeeId: string) => {
  const resignationDto: ResignationDto = {
    initiationDetails: {
      employeeId,
      resignationDate: "",
      personalEmail: "",
      grantTableDetails: [],
      saveDetails: false,
      deleteWithdrawnGrants: true,
    },
    pauseVestingDetails: {
      enablePauseVesting: false,
      pauseVestingDate: "",
    },
    lastWorkingDayDetails: {
      setLastWorkingDay: false,
      lastWorkingDate: "",
      lapsedImmediately: true,
    },
    exerciseOptionsDetails: {
      allowExercise: false,
      totalOptionsToExercise: 0,
      exerciseType: "",
      exerciseDate: "",
      fairMarketValue: 0,
      getPreview: true,
      validatePreview: false,
      exerciseOptions: false,
      grantDetailsForExercises: [],
    },
    extendExerciseDetails: {
      allowExtendExercise: false,
      grantDetailsForExercises: [],
    },
    fafLetterDetails: {
      generateLetter: false,
      grantsStatus: [],
      uploadFAFLetter: false,
      base64File: "",
      fileName: "",
      fileContentType: "",
      sendFAFLetterToEmployees: false,
      emailAddress: "",
      enableAcceptanceWindow: false,
      fafLetterStatus: FafLetterStatus.AcceptanceInitiated,
      sendLetterForApproval: false,
      signatureBase64: "",
      saveDetails: false,
      settlementLetterUrl: "",
      generationTime: new Date(),
    },
    accelerateVestingDetails: {
      enableAccelerateVesting: false,
      accelerateVestingForGrants: [],
    },
    finalGrantsStatus: [],
  };
  return resignationDto;
};
